import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { BookOpen, Check, X, Download, ChevronDown, ChevronLeft, ChevronRight, AlertCircle, Info, Trophy, Zap, Target, Clock, TrendingUp, Award, Lock, Star, Play, Pause, Home } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';   // ⬅️ importa la función
import { motion, AnimatePresence } from 'framer-motion';
import useSubscription from '../hooks/useSubscription';
import useQuizPersistence from '../hooks/useQuizPersistence';
import LoginModal from './LoginModal';
import PricingPlans from './PricingPlans';
import TTSControls from './TTSControls';
import SimulationResultsPanel from './SimulationResultsPanel';
import { computeExamAnalytics, buildExamHistoryPayload } from '../utils/examAnalytics';
import config from '../config';

// Funciones de ErrorBank
import {
  updateErrorBank,
  toggleLearned,
  updateMnemonic,
  getErrorBankData,
  addBulkSimulationErrors
} from './ErrorBank';

// 1. IMPORTAR COMPONENTES INTERACTIVOS PARA "Carcinoma de Tiroides"
import CarcinomadeTiroidesTable from '../InteractiveTables/CarcinomadeTiroidesTable';
import CarcinomadeTiroidesEsquema from '../InteractiveEsquemas/CarcinomadeTiroidesEsquema';
import NoduloTiroideoEsquema from '../InteractiveEsquemas/NoduloTiroideoEsquema';
import NoduloTiroideoTable from '../InteractiveTables/NoduloTiroideoTable';

// ==========================================================================
// COMPONENTES VISUALES MEJORADOS
// ==========================================================================

// Componente de Barra de Progreso — editorial premium
const ProgressBar = ({ current, total, correctAnswers, darkMode, darkModeStyle, simulationMode = false, isAnswered = false }) => {
  const percentage = (current / total) * 100;
  const correctPercentage = total > 0 ? (correctAnswers / total) * 100 : 0;

  // Tokens locales (paleta consistente con T del componente padre)
  const ink = darkMode ? '#F2EDE0' : '#0E1218';
  const inkSoft = darkMode ? '#B5AC9A' : '#3E4654';
  const inkMute = darkMode ? '#6A6356' : '#7A8499';
  const accent = darkMode ? '#7DA5D9' : '#1E3A6F';
  const sage = darkMode ? '#5CDCEF' : '#0596AC';   // cyan-aqua vivo premium
  const hairlineSoft = darkMode ? '#1A1F2A' : '#E5DFD2';
  const glow = darkMode ? 'rgba(125, 165, 217, 0.22)' : 'rgba(30, 58, 111, 0.16)';

  return (
    <div className="relative mb-5">
      <div className="flex justify-between items-baseline mb-2.5">
        <div className="flex items-baseline gap-3">
          <span
            className="font-mono"
            style={{
              color: ink,
              fontFamily: '"Geist Mono", monospace',
              fontVariantNumeric: 'tabular-nums',
              fontSize: '13px',
              fontWeight: 500,
              letterSpacing: '0.02em',
            }}
          >
            {String(current).padStart(2, '0')}
            <span style={{ color: inkMute, margin: '0 6px' }}>/</span>
            {String(total).padStart(2, '0')}
          </span>
          {simulationMode && (
            <span
              style={{
                fontFamily: '"Manrope", sans-serif',
                fontSize: '9.5px',
                fontWeight: 600,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                padding: '3px 8px',
                color: isAnswered ? accent : inkMute,
                background: 'transparent',
                border: `1px solid ${isAnswered ? accent : hairlineSoft}`,
                ...(isAnswered && darkMode && { boxShadow: `0 0 12px ${glow}` }),
              }}
            >
              {isAnswered ? '● respondida' : '○ pendiente'}
            </span>
          )}
        </div>
        {!simulationMode && (
          <span
            style={{
              fontFamily: '"Geist Mono", monospace',
              fontVariantNumeric: 'tabular-nums',
              fontSize: '12px',
              color: sage,
              fontWeight: 500,
              letterSpacing: '0.02em',
            }}
          >
            {correctAnswers} correctas
            <span style={{ color: inkMute, marginLeft: 6 }}>
              · {Math.round((correctAnswers / current) * 100 || 0)}%
            </span>
          </span>
        )}
      </div>
      <div
        style={{
          height: '3px',
          width: '100%',
          background: hairlineSoft,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <motion.div
          style={{
            position: 'absolute',
            inset: '0 auto 0 0',
            background: accent,
            ...(darkMode && { boxShadow: `0 0 8px ${glow}` }),
          }}
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        />
        {!simulationMode && (
          <motion.div
            style={{
              position: 'absolute',
              inset: '0 auto 0 0',
              background: sage,
              opacity: 0.85,
              mixBlendMode: 'normal',
            }}
            initial={{ width: 0 }}
            animate={{ width: `${correctPercentage}%` }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          />
        )}
      </div>
    </div>
  );
};

// Componente de Timer Circular — editorial premium
const CircularTimer = ({ timeLeft, totalTime, darkMode, isPaused, onPause, onResume }) => {
  const percentage = (timeLeft / totalTime) * 100;
  const radius = 32;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  // Tokens
  const ink = darkMode ? '#F2EDE0' : '#0E1218';
  const inkMute = darkMode ? '#6A6356' : '#7A8499';
  const hairlineSoft = darkMode ? '#1A1F2A' : '#E5DFD2';
  const accent = darkMode ? '#7DA5D9' : '#1E3A6F';
  const sage = darkMode ? '#5CDCEF' : '#0596AC';     // cyan-aqua vivo
  const crimson = darkMode ? '#F2604D' : '#D8311C';  // cinabrio coral vivo
  const paperSoft = darkMode ? '#0F131A' : '#FBF8F1';

  // Color según urgencia
  const getColor = () => {
    if (isPaused) return inkMute;
    if (percentage > 50) return accent;
    if (percentage > 20) return sage;
    return crimson;
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative">
        <svg width="74" height="74" className="transform -rotate-90">
          <circle cx="37" cy="37" r={radius} stroke={hairlineSoft} strokeWidth="2" fill="none" />
          <motion.circle
            cx="37"
            cy="37"
            r={radius}
            stroke={getColor()}
            strokeWidth="2"
            fill="none"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset }}
            transition={{ duration: 1, ease: 'linear' }}
            style={{
              filter: !isPaused && darkMode ? `drop-shadow(0 0 6px ${getColor()}60)` : 'none',
            }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <span
              className="block"
              style={{
                color: isPaused ? inkMute : (percentage <= 20 ? crimson : ink),
                fontFamily: '"Geist Mono", monospace',
                fontVariantNumeric: 'tabular-nums',
                fontSize: '14px',
                fontWeight: 500,
                letterSpacing: '0.02em',
              }}
            >
              {minutes}:{seconds.toString().padStart(2, '0')}
            </span>
            <span
              style={{
                fontFamily: '"Manrope", sans-serif',
                fontSize: '8px',
                fontWeight: 600,
                letterSpacing: '0.24em',
                textTransform: 'uppercase',
                color: inkMute,
              }}
            >
              total
            </span>
          </div>
        </div>
      </div>

      <button
        onClick={isPaused ? onResume : onPause}
        style={{
          padding: '4px 10px',
          fontFamily: '"Manrope", sans-serif',
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: isPaused ? sage : ink,
          background: paperSoft,
          border: `1px solid ${isPaused ? sage : hairlineSoft}`,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'all 0.18s',
        }}
        onMouseEnter={(e) => {
          if (isPaused) {
            e.currentTarget.style.background = sage;
            e.currentTarget.style.color = darkMode ? '#07090D' : paperSoft;
          } else {
            e.currentTarget.style.borderColor = accent;
            e.currentTarget.style.color = accent;
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = paperSoft;
          e.currentTarget.style.color = isPaused ? sage : ink;
          e.currentTarget.style.borderColor = isPaused ? sage : hairlineSoft;
        }}
      >
        {isPaused ? (
          <><Play className="w-2.5 h-2.5" /> reanudar</>
        ) : (
          <><Pause className="w-2.5 h-2.5" /> pausar</>
        )}
      </button>
    </div>
  );
};

// Timer por pregunta con indicador de color (verde → amarillo → rojo)
const QuestionTimer = ({ currentQuestion, isPaused, darkMode }) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => { setElapsed(0); }, [currentQuestion]);
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => setElapsed(prev => prev + 1), 1000);
    return () => clearInterval(interval);
  }, [isPaused, currentQuestion]);

  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;

  // Tokens (aqua → accent → cinabrio sin neón)
  const inkMute = darkMode ? '#6A6356' : '#7A8499';
  const hairlineSoft = darkMode ? '#1A1F2A' : '#E5DFD2';
  const accent = darkMode ? '#7DA5D9' : '#1E3A6F';
  const sage = darkMode ? '#5CDCEF' : '#0596AC';
  const crimson = darkMode ? '#F2604D' : '#D8311C';

  const getColor = () => {
    if (isPaused) return inkMute;
    if (elapsed <= 30) return sage;
    if (elapsed <= 60) return accent;
    return crimson;
  };

  const radius = 32;
  const circumference = 2 * Math.PI * radius;
  const maxTime = 90;
  const percentage = Math.min(elapsed / maxTime, 1);
  const strokeDashoffset = circumference - (percentage * circumference);
  const color = getColor();

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative">
        <svg width="74" height="74" className="transform -rotate-90">
          <circle cx="37" cy="37" r={radius} stroke={hairlineSoft} strokeWidth="2" fill="none" />
          <circle
            cx="37" cy="37" r={radius}
            stroke={color}
            strokeWidth="2"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={circumference - strokeDashoffset}
            style={{
              transition: 'stroke 0.5s, stroke-dashoffset 1s linear',
              filter: !isPaused && darkMode ? `drop-shadow(0 0 4px ${color}60)` : 'none',
            }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <span
              className="block"
              style={{
                color,
                fontFamily: '"Geist Mono", monospace',
                fontVariantNumeric: 'tabular-nums',
                fontSize: '14px',
                fontWeight: 500,
                letterSpacing: '0.02em',
              }}
            >
              {minutes}:{seconds.toString().padStart(2, '0')}
            </span>
            <span
              style={{
                fontFamily: '"Manrope", sans-serif',
                fontSize: '8px',
                fontWeight: 600,
                letterSpacing: '0.24em',
                textTransform: 'uppercase',
                color: inkMute,
              }}
            >
              preg.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente de Racha — chip editorial dopamínico
const StreakIndicator = ({ streak, darkMode }) => {
  if (streak < 2) return null;

  const ink = darkMode ? '#F2EDE0' : '#0E1218';
  const inkSoft = darkMode ? '#B5AC9A' : '#3E4654';
  const inkMute = darkMode ? '#6A6356' : '#7A8499';
  const sage = darkMode ? '#5CDCEF' : '#0596AC';     // cyan-aqua vivo
  const accent = darkMode ? '#7DA5D9' : '#1E3A6F';
  const paperSoft = darkMode ? '#0F131A' : '#FBF8F1';
  const sageGlow = darkMode ? 'rgba(92, 220, 239, 0.32)' : 'rgba(5, 150, 172, 0.22)';
  const glow = darkMode ? 'rgba(125, 165, 217, 0.22)' : 'rgba(30, 58, 111, 0.16)';

  const getStreakLevel = () => {
    if (streak >= 10) return { color: accent, glow: glow, label: 'racha excepcional' };
    if (streak >= 5) return { color: sage, glow: sageGlow, label: 'en racha' };
    return { color: inkSoft, glow: 'transparent', label: 'racha' };
  };

  const { color, glow: levelGlow, label } = getStreakLevel();

  return (
    <motion.div
      initial={{ scale: 0.92, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.92, opacity: 0 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: '8px',
        padding: '5px 12px',
        background: paperSoft,
        border: `1px solid ${color}`,
        ...(darkMode && streak >= 5 ? { boxShadow: `0 0 16px ${levelGlow}` } : {}),
      }}
    >
      <span
        style={{
          fontFamily: '"Geist Mono", monospace',
          fontVariantNumeric: 'tabular-nums',
          fontSize: '14px',
          fontWeight: 500,
          color: color,
          letterSpacing: '0.02em',
        }}
      >
        ×{streak}
      </span>
      <span
        style={{
          fontFamily: '"Manrope", sans-serif',
          fontSize: '9.5px',
          fontWeight: 600,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: inkMute,
        }}
      >
        {label}
      </span>
    </motion.div>
  );
};

// Efecto Ripple — sutil pulso accent al click
const RippleEffect = ({ isActive, darkMode }) => {
  if (!isActive) return null;
  const accent = darkMode ? '#7DA5D9' : '#1E3A6F';

  return (
    <motion.div
      initial={{ scale: 0.95, opacity: 0.35 }}
      animate={{ scale: 1.6, opacity: 0 }}
      transition={{ duration: 0.55, ease: 'easeOut' }}
      className="absolute inset-0 pointer-events-none"
      style={{
        background: `radial-gradient(circle, ${accent}30 0%, transparent 70%)`,
      }}
    />
  );
};

// DarkModeStyleSelector — eliminado (un solo modo oscuro premium)
const DarkModeStyleSelector = () => null;

// ShimmerEffect — desactivado (queda como noop). Sustituido por glow accent en la option directamente.
const ShimmerEffect = () => null;

// CelebrationBurst — sutil pulso accent (sin emojis). Dopamina elegante.
const CelebrationBurst = ({ show }) => {
  if (!show) return null;

  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1.05, opacity: [0, 0.6, 0] }}
      transition={{ duration: 0.9, ease: 'easeOut' }}
      className="absolute inset-0 pointer-events-none"
      style={{
        background: 'radial-gradient(circle at center, rgba(141, 162, 148, 0.18) 0%, transparent 65%)',
        mixBlendMode: 'screen',
      }}
    />
  );
};

// Componente de Estadísticas — tabular editorial
const RealTimeStats = ({ totalQuestions, correctAnswers, incorrectAnswers, timeSpent, darkMode, darkModeStyle }) => {
  const answered = correctAnswers + incorrectAnswers;
  const accuracy = answered > 0 ? Math.round((correctAnswers / answered) * 100) : 0;
  const avgTimePerQuestion = answered > 0 ? Math.round(timeSpent / answered) : 0;

  const ink = darkMode ? '#F2EDE0' : '#0E1218';
  const inkMute = darkMode ? '#6A6356' : '#7A8499';
  const hairlineSoft = darkMode ? '#1A1F2A' : '#E5DFD2';
  const hairline = darkMode ? '#2B3140' : '#D8D2C5';
  const accent = darkMode ? '#7DA5D9' : '#1E3A6F';
  const sage = darkMode ? '#5CDCEF' : '#0596AC';     // cyan-aqua vivo
  const crimson = darkMode ? '#F2604D' : '#D8311C';  // cinabrio coral vivo
  const paperSoft = darkMode ? '#0F131A' : '#FBF8F1';

  const StatCell = ({ label, value, color, last = false }) => (
    <div
      className="px-4 py-3"
      style={{ borderRight: last ? 'none' : `1px solid ${hairlineSoft}` }}
    >
      <div
        style={{
          fontFamily: '"Manrope", sans-serif',
          fontSize: '9.5px',
          fontWeight: 600,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: inkMute,
          marginBottom: '4px',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: '"Geist Mono", monospace',
          fontVariantNumeric: 'tabular-nums',
          fontSize: '20px',
          fontWeight: 500,
          color: color,
          lineHeight: 1,
          letterSpacing: '-0.01em',
        }}
      >
        {value}
      </div>
    </div>
  );

  return (
    <div
      className="grid grid-cols-4"
      style={{
        background: paperSoft,
        border: `1px solid ${hairline}`,
        ...(darkMode ? { boxShadow: `inset 0 1px 0 rgba(242,237,224,0.025)` } : { boxShadow: `inset 0 1px 0 rgba(255,255,255,0.5)` }),
      }}
    >
      <StatCell label="correctas" value={correctAnswers} color={sage} />
      <StatCell label="incorrectas" value={incorrectAnswers} color={incorrectAnswers > 0 ? crimson : ink} />
      <StatCell label="precisión" value={`${accuracy}%`} color={accuracy >= 70 ? sage : (accuracy >= 50 ? accent : crimson)} />
      <StatCell label="promedio" value={`${avgTimePerQuestion}s`} color={ink} last />
    </div>
  );
};

/**
 * PROPS:
 *   - title, questions, onBack, DecisionTableComponents, simulationMode, subjectName,
 *     topicName, simulationTime, fromGenerator
 */
function Quiz({
  title,
  questions,
  onBack,
  onGoHome,
  DecisionTableComponents,
  simulationMode,
  subjectName,
  topicName,
  simulationTime,
  fromGenerator,
  darkMode,
  resumeQuizId,
  preserveOrder = false, // Si es true, no aleatoriza las preguntas aunque sea simulationMode
  simulacroId = null,
  // Props opcionales para tracking de Práctica Continua
  onQuestionStart,
  onQuestionAnswered,
  skipHistorySave = false,
  // Resume desde MySQL (Historial de Exámenes)
  resumeFromMySQL = null
}) {
  // Generar ID único para este quiz
  const quizId = useMemo(() => {
    if (resumeQuizId) return resumeQuizId;
    return `${title}_${subjectName || 'general'}_${simulationMode ? 'simulation' : 'study'}_${questions?.length || 0}`;
  }, [resumeQuizId, title, subjectName, simulationMode, questions]);

  // Hook de persistencia
  const {
    saveStateImmediate,
    restoreState,
    clearSavedState,
    scheduleAutosave,
    updateStateRef,
    isRestoring,
    cancelAutosave
  } = useQuizPersistence(quizId);

  // -------------------------------------------------------------------------
  // PERSISTENCIA DE ESTADO: Restaurar estado al montar
  // -------------------------------------------------------------------------
  // El hook ya está declarado arriba, solo necesitamos restaurar el estado
  const restoredState = useMemo(() => restoreState(), [restoreState]);

  // -------------------------------------------------------------------------
  // 1) Timer para modo simulacro con persistencia y PAUSA
  // -------------------------------------------------------------------------
  const totalTimeInSeconds = simulationMode ? simulationTime * 60 : null;
  const [timeLeft, setTimeLeft] = useState(() =>
    restoredState?.timeLeft || totalTimeInSeconds
  );

  // Estados para el sistema de pausa
  const [isPaused, setIsPaused] = useState(() =>
    restoredState?.isPaused || false
  );
  const [pauseStartTime, setPauseStartTime] = useState(null);
  const [totalPausedTime, setTotalPausedTime] = useState(() =>
    restoredState?.totalPausedTime || 0
  );
  const timerIntervalRef = useRef(null);

  // -------------------------------------------------------------------------
  // 2) Mostrar resultados al final
  // -------------------------------------------------------------------------
  const [showResults, setShowResults] = useState(() =>
    restoredState?.showResults || false
  );

  // -------------------------------------------------------------------------
  // 3) Preguntas incorrectas acumuladas con persistencia
  // -------------------------------------------------------------------------
  const [incorrectQuestions, setIncorrectQuestions] = useState(() =>
    restoredState?.incorrectQuestions || []
  );

  // -------------------------------------------------------------------------
  // 4) Manejo de pregunta actual con persistencia
  // -------------------------------------------------------------------------
  const [currentQuestion, setCurrentQuestion] = useState(() =>
    restoredState?.currentQuestion || 0
  );
  const [selectedAnswer, setSelectedAnswer] = useState(() =>
    restoredState?.selectedAnswer || null
  );
  const [showExplanation, setShowExplanation] = useState(() =>
    restoredState?.showExplanation || false
  );
  const [reviewMode, setReviewMode] = useState(false);
  const [showDecisionTable, setShowDecisionTable] = useState(false);
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [attemptedIncorrect, setAttemptedIncorrect] = useState(() =>
    restoredState?.attemptedIncorrect || []
  ); // Track incorrect attempts
  
  // -------------------------------------------------------------------------
  // 4.1) Estados para mejoras visuales
  // -------------------------------------------------------------------------
  const [streak, setStreak] = useState(() => {
    return restoredState?.streak || 0;
  });
  const [correctAnswersCount, setCorrectAnswersCount] = useState(() => {
    return restoredState?.correctAnswersCount || 0;
  });
  const [incorrectAnswersCount, setIncorrectAnswersCount] = useState(() => {
    return restoredState?.incorrectAnswersCount || 0;
  });
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [totalTimeSpent, setTotalTimeSpent] = useState(0);
  const [celebrationOptionId, setCelebrationOptionId] = useState(null);
  const [animateQuestion, setAnimateQuestion] = useState(true);
  const [rippleOptionId, setRippleOptionId] = useState(null);
  const [showPauseOverlay, setShowPauseOverlay] = useState(false);
  const autoAdvanceTimeoutRef = useRef(null);
  const [finishPromptSnapshot, setFinishPromptSnapshot] = useState(null);
  // Estados de simulacro adicionales (paleta, marcados, extra tiempo)
  const [showPalette, setShowPalette] = useState(false);
  const [paletteOnlyUnanswered, setPaletteOnlyUnanswered] = useState(false);
  const [markedForReview, setMarkedForReview] = useState(() => ({}));
  const [extraTime, setExtraTime] = useState(0);
  // Notas de coaching por pregunta: { [questionIndex]: { text, confidence } }
  const [questionNotes, setQuestionNotes] = useState(() => ({}));
  // Tiempo por pregunta en segundos: { [questionIndex]: seconds }
  const [questionTimes, setQuestionTimes] = useState(() => ({}));
  const [showNotePanel, setShowNotePanel] = useState(false);
  // Flag para saber si ya restauramos el estado del quiz
  const [hasRestored, setHasRestored] = useState(false);
  
  // Estado para el estilo del modo oscuro (A: glassmorphism, B: neon)
  const [darkModeStyle, setDarkModeStyle] = useState(() => {
    const saved = localStorage.getItem('darkModeStyle');
    return saved || 'A'; // Por defecto Opción A (Glass minimal)
  });


  // Guardar preferencia de estilo cuando cambie
  useEffect(() => {
    localStorage.setItem('darkModeStyle', darkModeStyle);
  }, [darkModeStyle]);

  useEffect(() => {
    return () => {
      if (autoAdvanceTimeoutRef.current) {
        clearTimeout(autoAdvanceTimeoutRef.current);
      }
    };
  }, []);

  // NOTA: El useEffect de AUTO-GUARDAR se movió después de todas las declaraciones de estado
  // para evitar el error "Cannot access before initialization"
  
  // -------------------------------------------------------------------------
  // 4.2) Estados para manejo de suscripción y límites
  // -------------------------------------------------------------------------
  const { 
    user, 
    subscription, 
    canAnswerQuestion, 
    showUpgradePrompt,
    trackQuestionAnswered 
  } = useSubscription();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [questionsAnswered, setQuestionsAnswered] = useState(() =>
    restoredState?.questionsAnswered || 0
  );
  
  // -------------------------------------------------------------------------
  // 5) Historial local de estudio (localStorage)
  // -------------------------------------------------------------------------
  const [studyHistory, setStudyHistory] = useState(() => {
    try {
      const saved = localStorage.getItem(`studyHistory_${title}`);
      return saved
        ? JSON.parse(saved)
        : {
            attempts: 0,
            correct: 0,
            questionHistory: {}
          };
    } catch {
      return {
        attempts: 0,
        correct: 0,
        questionHistory: {}
      };
    }
  });

  const [examHistory, setExamHistory] = useState(() => {
    try {
      const stored = localStorage.getItem('examHistory');
      return stored ? JSON.parse(stored) : [];
    } catch (_) {
      return [];
    }
  });
  const [savedHistory, setSavedHistory] = useState(false);
  // Save-and-continue: track MySQL session ID for in-progress exams
  const [savedSessionId, setSavedSessionId] = useState(null);
  const [saveProgressMsg, setSaveProgressMsg] = useState(null);
  const examStartedAtRef = useRef(new Date().toISOString());
  const isSavingRef = useRef(false);
  const autoSaveDebounceRef = useRef(null);
  const autoSaveIntervalRef = useRef(null);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [selectedHistoryExam, setSelectedHistoryExam] = useState(null);
  const historyStats = useMemo(() => {
    if (!examHistory.length) {
      return { attempts: 0, average: 0, best: null };
    }
    const totalScore = examHistory.reduce((acc, exam) => acc + (exam.scorePercentage || 0), 0);
    const bestExam = examHistory.reduce(
      (best, exam) =>
        (exam.scorePercentage || 0) > (best?.scorePercentage || 0) ? exam : best,
      examHistory[0]
    );
    return {
      attempts: examHistory.length,
      average: Math.round(totalScore / examHistory.length),
      best: bestExam,
    };
  }, [examHistory]);

  // -------------------------------------------------------------------------
  // 6) Filtros en modo estudio
  // -------------------------------------------------------------------------
  const [selectedYear, setSelectedYear] = useState('Todos');
  const [selectedSubtopic, setSelectedSubtopic] = useState('Todos');
  // Selección múltiple de preguntas (set de IDs numéricos)
  const [selectedQuestionIds, setSelectedQuestionIds] = useState(() => new Set());
  const [sortByYear, setSortByYear] = useState(false);
  // Combobox de preguntas
  const [showQuestionDropdown, setShowQuestionDropdown] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState('');
  const questionDropdownRef = useRef(null);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    if (!showQuestionDropdown) return;
    const handler = (e) => {
      if (questionDropdownRef.current && !questionDropdownRef.current.contains(e.target)) {
        setShowQuestionDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showQuestionDropdown]);

  const years = [...new Set(questions.map((q) => q.year))];
  const subtopics = [...new Set(questions.map((q) => q.subtopic || 'Sin subtema'))];

  // -------------------------------------------------------------------------
  // 7) Barajar preguntas si es simulacro (con persistencia)
  // -------------------------------------------------------------------------
  // Inicializar inmediatamente con las preguntas disponibles
  const [shuffledQuestions, setShuffledQuestions] = useState(() => {
    // Inicializar desde estado guardado si existe para evitar parpadeo/reordenamiento
    try {
      const raw = localStorage.getItem(`mediexampro_quiz_state_${quizId}`);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && saved.quizId === quizId && Array.isArray(saved.questionOrder) && questions.length > 0) {
          const ordered = saved.questionOrder
            .map(qId => questions.find(q => q.id === qId))
            .filter(Boolean);
          if (ordered.length > 0) {
            return ordered;
          }
        }
      }
    } catch (_) {}

    if (questions.length === 0) return [];
    // Solo aleatorizar si es simulationMode Y NO se quiere preservar el orden
    if (simulationMode && !preserveOrder) {
      return [...questions].sort(() => Math.random() - 0.5);
    }
    return questions;
  });
  const [hasInitializedQuestions, setHasInitializedQuestions] = useState(questions.length > 0);

  // -------------------------------------------------------------------------
  // 8) Aplicar filtros
  // -------------------------------------------------------------------------
  const filteredByYear =
    selectedYear === 'Todos'
      ? shuffledQuestions
      : shuffledQuestions.filter((q) => q.year === selectedYear);

  // Preguntas disponibles después de filtros año + subtema (ANTES del filtro por ID).
  // Esto es lo que muestra el combobox del lookup por ID.
  const availableQuestionsForLookup = useMemo(() => {
    return filteredByYear.filter((q) =>
      selectedSubtopic === 'Todos' ? true : q.subtopic === selectedSubtopic
    );
  }, [filteredByYear, selectedSubtopic]);

  // Helper para extraer la parte numérica del id (sim_9543 → "9543")
  const extractNumericId = (rawId) => {
    const s = String(rawId || '');
    const m = s.match(/\d+/);
    return m ? m[0] : s;
  };

  const filteredQuestions = useMemo(() => {
    let filtered = availableQuestionsForLookup;

    // Filtro por selección múltiple de IDs
    if (selectedQuestionIds.size > 0) {
      filtered = filtered.filter((q) => {
        const numId = extractNumericId(q.id);
        return selectedQuestionIds.has(numId);
      });
    }

    // Ordenar por Año si está activado (solo en modo estudio)
    if (sortByYear && !simulationMode) {
      filtered = [...filtered].sort((a, b) => {
        const yearA = parseInt(a.year?.match(/\d{4}/)?.[0] || a.year || '0');
        const yearB = parseInt(b.year?.match(/\d{4}/)?.[0] || b.year || '0');
        if (isNaN(yearA) || isNaN(yearB)) return 0;
        return yearB - yearA;
      });
    }

    return filtered;
  }, [availableQuestionsForLookup, selectedQuestionIds, sortByYear, simulationMode]);

  // Preguntas que matchean la búsqueda dentro del dropdown
  // (busca por ID, año, subtema, texto del enunciado o del caso clínico)
  const dropdownMatchingQuestions = useMemo(() => {
    if (!dropdownSearch.trim()) return availableQuestionsForLookup;
    const q = dropdownSearch.trim().toLowerCase();
    return availableQuestionsForLookup.filter((item) => {
      const id = String(item.id || '').toLowerCase();
      const text = (item.question_text || '').toLowerCase();
      const cd = (item.case_description || '').toLowerCase();
      const yr = String(item.year || '').toLowerCase();
      const st = (item.subtopic || '').toLowerCase();
      const sps = (item.subtopic_specific || '').toLowerCase();
      return (
        id.includes(q) || text.includes(q) || cd.includes(q) ||
        yr.includes(q) || st.includes(q) || sps.includes(q)
      );
    });
  }, [availableQuestionsForLookup, dropdownSearch]);

  // Toggle individual de selección
  const toggleQuestionSelection = (rawId) => {
    const numId = extractNumericId(rawId);
    setSelectedQuestionIds((prev) => {
      const next = new Set(prev);
      if (next.has(numId)) next.delete(numId);
      else next.add(numId);
      return next;
    });
    setCurrentQuestion(0);
  };

  // Seleccionar todas las visibles en el dropdown (respetando dropdownSearch)
  const selectAllVisibleInDropdown = () => {
    setSelectedQuestionIds((prev) => {
      const next = new Set(prev);
      dropdownMatchingQuestions.forEach((q) => next.add(extractNumericId(q.id)));
      return next;
    });
    setCurrentQuestion(0);
  };

  // Deseleccionar las visibles
  const deselectAllVisibleInDropdown = () => {
    setSelectedQuestionIds((prev) => {
      const next = new Set(prev);
      dropdownMatchingQuestions.forEach((q) => next.delete(extractNumericId(q.id)));
      return next;
    });
    setCurrentQuestion(0);
  };

  // Limpiar TODA la selección
  const clearAllQuestionSelections = () => {
    setSelectedQuestionIds(new Set());
    setDropdownSearch('');
    setCurrentQuestion(0);
  };

  // ¿Cuántas de las visibles ya están seleccionadas?
  const visibleSelectedCount = useMemo(() => {
    let n = 0;
    for (const q of dropdownMatchingQuestions) {
      if (selectedQuestionIds.has(extractNumericId(q.id))) n++;
    }
    return n;
  }, [dropdownMatchingQuestions, selectedQuestionIds]);

  // Ajustar currentQuestion si excede el nuevo tamaño tras filtrar, evitando interferir durante restauración
  const isApplyingRestoreRef = useRef(false);
  useEffect(() => {
    if (!hasRestored || isRestoring || isApplyingRestoreRef.current) return;
    if (filteredQuestions.length > 0 && currentQuestion >= filteredQuestions.length) {
      setCurrentQuestion(Math.max(0, filteredQuestions.length - 1));
    }
  }, [filteredQuestions.length, hasRestored, isRestoring, currentQuestion]);

  // -------------------------------------------------------------------------
  // 9) Guardar studyHistory en localStorage
  // -------------------------------------------------------------------------
  useEffect(() => {
    try {
      localStorage.setItem(`studyHistory_${title}`, JSON.stringify(studyHistory));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }, [studyHistory, title]);

  useEffect(() => {
    if (showResults && simulationMode) {
      try {
        const stored = localStorage.getItem('examHistory');
        setExamHistory(stored ? JSON.parse(stored) : []);
      } catch (_) {
        setExamHistory([]);
      }
    }
  }, [showResults, simulationMode]);

  useEffect(() => {
    if (!showResults) {
      setSavedHistory(false);
    }
  }, [showResults]);

  useEffect(() => {
    if (examHistory.length === 0) {
      setSelectedHistoryExam(null);
      return;
    }
    setSelectedHistoryExam((prev) => {
      if (!prev) return examHistory[0];
      const match = examHistory.find((item) => item.id === prev.id);
      return match || examHistory[0];
    });
  }, [examHistory]);

  // (El temporizador original se elimina en favor del sistema mejorado con pausa)

  // Respuestas del usuario (para revisión final en modo simulacro)
const [userAnswers, setUserAnswers] = useState(() =>
  restoredState?.userAnswers || []
);

  const totalAllocatedSeconds = useMemo(() => {
    if (!simulationMode) return 0;
    const base = (simulationTime || 0) * 60;
    return base + (extraTime || 0);
  }, [simulationMode, simulationTime, extraTime]);

  const timeSpentSeconds = useMemo(() => {
    if (!simulationMode) return totalTimeSpent;
    if (totalTimeSpent > 0) return totalTimeSpent;
    if (totalAllocatedSeconds > 0 && timeLeft !== null && timeLeft !== undefined) {
      return Math.max(totalAllocatedSeconds - timeLeft, 0);
    }
    return totalTimeSpent;
  }, [simulationMode, totalTimeSpent, totalAllocatedSeconds, timeLeft]);

  const examAnalytics = useMemo(() => {
    if (!showResults || !simulationMode) return null;
    return computeExamAnalytics({
      questions: filteredQuestions,
      userAnswers,
      markedForReview,
      questionNotes,
      questionTimes,
    });
  }, [showResults, simulationMode, filteredQuestions, userAnswers, markedForReview, questionNotes, questionTimes]);

  // -------------------------------------------------------------------------
  // 11) Pregunta actual
  // -------------------------------------------------------------------------
  const currentQuestionData = filteredQuestions[currentQuestion] || null;
  const currentQuestionOptions = currentQuestionData?.options || [];
  const currentAnswerId = userAnswers[currentQuestion] ?? null;
  const isCurrentAnswered = currentAnswerId !== null && currentAnswerId !== undefined;

  const answeredCount = useMemo(
    () => userAnswers.filter((value) => value !== null && value !== undefined).length,
    [userAnswers]
  );

  const keypadLayout = useMemo(() => {
    const labels = ['A', 'B', 'C', 'D', 'E'];
    return labels.map((label) => {
      const option = currentQuestionOptions.find(
        (opt) => (opt?.option_label || '').toUpperCase() === label
      );
      return {
        label,
        optionId: option?.id ?? null,
        disabled: !option,
      };
    });
  }, [currentQuestionOptions]);

  const orderedOptionsByLabel = useMemo(
    () =>
      [...currentQuestionOptions].sort((a, b) =>
        (a?.option_label || '').localeCompare(b?.option_label || '')
      ),
    [currentQuestionOptions]
  );

  // -------------------------------------------------------------------------
  // Restaurar estado al montar el componente
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!hasRestored && !isRestoring && questions.length > 0) {
      isApplyingRestoreRef.current = true;

      // PRIORIDAD 1: Resume desde MySQL (cuando viene del Historial de Exámenes)
      if (resumeFromMySQL) {
        console.log('🔄 Restaurando desde MySQL (session ID:', resumeFromMySQL.sessionId, ')');
        // Las preguntas ya vienen en el orden correcto desde el endpoint de resume
        setShuffledQuestions(questions);
        setHasInitializedQuestions(true);

        // Restaurar estado del quiz
        setCurrentQuestion(resumeFromMySQL.currentQuestion || 0);
        setUserAnswers(resumeFromMySQL.userAnswers || []);
        setCorrectAnswersCount(resumeFromMySQL.correctAnswersCount || 0);
        setIncorrectAnswersCount(resumeFromMySQL.incorrectAnswersCount || 0);
        setQuestionNotes(resumeFromMySQL.questionNotes || {});
        setMarkedForReview(resumeFromMySQL.markedForReview || {});
        setQuestionTimes(resumeFromMySQL.questionTimes || {});

        // Contar preguntas respondidas
        const answered = (resumeFromMySQL.userAnswers || []).filter(a => a != null).length;
        setQuestionsAnswered(answered);

        // Vincular con la sesión MySQL para futuros "Guardar"
        setSavedSessionId(resumeFromMySQL.sessionId);

        // Calcular tiempo restante
        if (simulationMode && resumeFromMySQL.timeAllocated) {
          const remaining = Math.max(0, resumeFromMySQL.timeAllocated - (resumeFromMySQL.timeSpent || 0));
          setTimeLeft(remaining);
        }

        setShowExplanation(false);
        setSelectedAnswer(null);

        console.log(`✅ MySQL resume completo: pregunta ${resumeFromMySQL.currentQuestion + 1}, ${answered} respondidas`);
      }
      // PRIORIDAD 2: Resume desde localStorage (comportamiento original)
      else {
        const savedState = restoreState();
        if (savedState && savedState.quizId === quizId && savedState.questionOrder) {
          console.log('🔄 Restaurando estado del quiz guardado');

          const orderedQuestions = savedState.questionOrder
            .map(qId => questions.find(q => q.id === qId))
            .filter(Boolean);

          if (orderedQuestions.length > 0) {
            setShuffledQuestions(orderedQuestions);
            setHasInitializedQuestions(true);
            console.log('🎲 Orden de preguntas restaurado:', orderedQuestions.length, 'preguntas');

            if (savedState.currentQuestion !== undefined) setCurrentQuestion(savedState.currentQuestion);
            if (savedState.userAnswers) setUserAnswers(savedState.userAnswers);
            if (savedState.correctAnswersCount !== undefined) setCorrectAnswersCount(savedState.correctAnswersCount);
            if (savedState.incorrectAnswersCount !== undefined) setIncorrectAnswersCount(savedState.incorrectAnswersCount);
            if (savedState.streak !== undefined) setStreak(savedState.streak);
            if (savedState.incorrectQuestions) setIncorrectQuestions(savedState.incorrectQuestions);
            if (savedState.attemptedIncorrect) setAttemptedIncorrect(savedState.attemptedIncorrect);
            if (savedState.questionsAnswered !== undefined) setQuestionsAnswered(savedState.questionsAnswered);
            if (savedState.totalTimeSpent !== undefined) setTotalTimeSpent(savedState.totalTimeSpent);

            if (savedState.selectedYear) setSelectedYear(savedState.selectedYear);
            if (savedState.selectedSubtopic) setSelectedSubtopic(savedState.selectedSubtopic);

            if (simulationMode && savedState.timeLeft !== undefined) {
              const elapsedTime = Math.floor((Date.now() - savedState.timestamp) / 1000);
              const adjustedTimeLeft = savedState.isPaused
                ? savedState.timeLeft
                : Math.max(0, savedState.timeLeft - elapsedTime);
              setTimeLeft(adjustedTimeLeft);
            }
          }

          if (savedState.questionNotes) setQuestionNotes(savedState.questionNotes);
          if (savedState.markedForReview) setMarkedForReview(savedState.markedForReview);

          setShowExplanation(false);
          setSelectedAnswer(null);
        } else {
          console.log('🆕 Nueva Sesión de quiz');
        }
      }

      setHasRestored(true);
      setTimeout(() => {
        isApplyingRestoreRef.current = false;
      }, 0);
    }
  }, [hasRestored, isRestoring, restoreState, quizId, simulationMode, questions.length, resumeFromMySQL]);

  // -------------------------------------------------------------------------
  // Vincular con sesión in_progress existente en MySQL (evita duplicados)
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!simulationMode || !hasRestored || savedSessionId) return;
    const apiUrl = config.getApiUrl();
    fetch(`${apiUrl}/exam-sessions/in-progress`)
      .then(res => res.json())
      .then(data => {
        if (data.found && data.session?.id) {
          setSavedSessionId(data.session.id);
          console.log(`[SaveProgress] Vinculado con sesión existente ID=${data.session.id}`);
        }
      })
      .catch(() => {}); // silencioso si el server no responde
  }, [simulationMode, hasRestored, savedSessionId]);

  // -------------------------------------------------------------------------
  // AUTO-GUARDAR ESTADO DEL QUIZ EN LOCALSTORAGE
  // -------------------------------------------------------------------------
  useEffect(() => {
    // Solo guardar si tenemos preguntas y el quiz está activo
    if (filteredQuestions.length > 0 && !showResults) {
      const stateToSave = {
        quizId,
        currentQuestion,
        selectedAnswer,
        showExplanation,
        userAnswers,
        incorrectQuestions,
        attemptedIncorrect,
        streak,
        correctAnswersCount,
        incorrectAnswersCount,
        timeLeft,
        showResults,
        questionsAnswered,
        selectedYear,
        selectedSubtopic,
        isPaused,
        totalPausedTime,
        simulationMode,
        simulationTime,
        title,
        // simulacro extra
        simulacroId,
        markedForReview,
        questionNotes,
        extraTime,
        questionOrder: shuffledQuestions.map(q => q.id),
        timestamp: Date.now()
      };

      // Actualizar referencia y programar autosave
      updateStateRef(stateToSave);
      scheduleAutosave(stateToSave);
    }
  }, [
    quizId, currentQuestion, selectedAnswer, showExplanation, userAnswers,
    incorrectQuestions, attemptedIncorrect, streak, correctAnswersCount,
    incorrectAnswersCount, timeLeft, showResults, questionsAnswered,
    selectedYear, selectedSubtopic, filteredQuestions.length,
    isPaused, totalPausedTime, updateStateRef, scheduleAutosave
  ]);

  // -------------------------------------------------------------------------
  // FUNCIONES DE PAUSA/REANUDACIÓN PARA MODO SIMULACRO
  // -------------------------------------------------------------------------

  const handlePause = useCallback(
    (options = {}) => {
      const { showOverlay = true } = options;

      if (isPaused) {
        if (!showOverlay) {
          setShowPauseOverlay(false);
        }
        return;
      }

      console.log('🛑 Pausando timer...');
      setIsPaused(true);
      setPauseStartTime(Date.now());
      setShowPauseOverlay(showOverlay);
      setShowPalette(false);

      if (shuffledQuestions.length > 0) {
        const newState = {
          quizId,
          currentQuestion,
          userAnswers,
          correctAnswersCount,
          incorrectAnswersCount,
          streak,
          incorrectQuestions,
          attemptedIncorrect,
          questionsAnswered,
          totalTimeSpent,
          selectedYear,
          selectedSubtopic,
          timeLeft,
          showResults,
          isPaused: true,
          totalPausedTime,
          simulationMode,
          simulationTime,
          title,
          simulacroId,
          questionOrder: shuffledQuestions.map((q) => q.id),
          timestamp: Date.now()
        };
        saveStateImmediate(newState);
      }
    },
    [
      attemptedIncorrect,
      currentQuestion,
      incorrectAnswersCount,
      incorrectQuestions,
      isPaused,
      questionsAnswered,
      quizId,
      saveStateImmediate,
      selectedSubtopic,
      selectedYear,
      showResults,
      shuffledQuestions,
      simulationMode,
      simulationTime,
      streak,
      timeLeft,
      title,
      totalPausedTime,
      totalTimeSpent,
      userAnswers,
      correctAnswersCount
    ]
  );

  const handleResume = useCallback(() => {
    if (!isPaused) return;

    console.log('▶️ Reanudando timer...');
    const pausedDuration = Date.now() - pauseStartTime;
    setTotalPausedTime((prev) => prev + pausedDuration);
    setPauseStartTime(null);
    setIsPaused(false);
    setShowPauseOverlay(false);
    // Resetear questionStartTime para que la pausa no cuente en time_spent_seconds
    setQuestionStartTime(Date.now());

    if (shuffledQuestions.length > 0) {
      const newState = {
        quizId,
        currentQuestion,
        userAnswers,
        correctAnswersCount,
        incorrectAnswersCount,
        streak,
        incorrectQuestions,
        attemptedIncorrect,
        questionsAnswered,
        totalTimeSpent,
        selectedYear,
        selectedSubtopic,
        timeLeft,
        showResults,
        isPaused: false,
        totalPausedTime: totalPausedTime + pausedDuration,
        simulationMode,
        simulationTime,
        title,
        simulacroId,
        questionOrder: shuffledQuestions.map((q) => q.id),
        timestamp: Date.now()
      };
      saveStateImmediate(newState);
    }
  }, [
    attemptedIncorrect,
    correctAnswersCount,
    currentQuestion,
    incorrectAnswersCount,
    incorrectQuestions,
    isPaused,
    pauseStartTime,
    questionsAnswered,
    quizId,
    saveStateImmediate,
    selectedSubtopic,
    selectedYear,
    showResults,
    shuffledQuestions,
    simulationMode,
    simulationTime,
    streak,
    timeLeft,
    title,
    totalPausedTime,
    totalTimeSpent,
    userAnswers
  ]);

  // -------------------------------------------------------------------------
  // Helpers de simulacro (persistencia y respuestas rápidas)
  // -------------------------------------------------------------------------
  const persistQuizState = useCallback(
    (overrides = {}) => {
      try {
        saveStateImmediate({
          quizId,
          currentQuestion,
          userAnswers,
          correctAnswersCount,
          incorrectAnswersCount,
          streak,
          incorrectQuestions,
          attemptedIncorrect,
          questionsAnswered,
          totalTimeSpent,
          selectedYear,
          selectedSubtopic,
          timeLeft,
          showResults,
          isPaused,
          totalPausedTime,
          simulationMode,
          simulationTime,
          title,
          simulacroId,
          markedForReview,
          questionNotes,
          extraTime,
          questionOrder: shuffledQuestions.map((q) => q.id),
          timestamp: Date.now(),
          ...overrides,
        });
      } catch (_) {}
    },
    [
      saveStateImmediate,
      quizId,
      currentQuestion,
      userAnswers,
      correctAnswersCount,
      incorrectAnswersCount,
      streak,
      incorrectQuestions,
      attemptedIncorrect,
      questionsAnswered,
      totalTimeSpent,
      selectedYear,
      selectedSubtopic,
      timeLeft,
      showResults,
      isPaused,
      totalPausedTime,
      simulationMode,
      simulationTime,
      title,
      markedForReview,
      extraTime,
      shuffledQuestions,
    ]
  );

  const computeSimulationSnapshot = useCallback(
    (answersOverride, { includeIncorrectList = true } = {}) => {
      const answers = Array.isArray(answersOverride) ? answersOverride : userAnswers;
      const total = filteredQuestions.length;
      let answered = 0;
      let correct = 0;
      const incorrectList = [];

      filteredQuestions.forEach((question, index) => {
        if (!question) return;
        const selectedId = answers?.[index];
        if (selectedId === null || selectedId === undefined) {
          return;
        }

        answered += 1;
        const isCorrect = (question.options || []).some(
          (opt) => opt && opt.id === selectedId && opt.correct
        );

        if (isCorrect) {
          correct += 1;
        } else if (includeIncorrectList) {
          incorrectList.push(question);
        }
      });

      const incorrect = answered - correct;
      const skipped = total - answered;

      return {
        total,
        answered,
        correct,
        incorrect,
        skipped,
        accuracy: total > 0 ? Math.round((correct / total) * 100) : 0,
        incorrectQuestions: includeIncorrectList ? incorrectList : [],
      };
    },
    [filteredQuestions, userAnswers]
  );

  const openFinishPrompt = useCallback(
    (snapshot) => {
      if (!snapshot) return;
      if (autoAdvanceTimeoutRef.current) {
        clearTimeout(autoAdvanceTimeoutRef.current);
      }
      setShowPalette(false);
      setFinishPromptSnapshot({
        ...snapshot,
        id: Date.now(),
      });
    },
    [setShowPalette, setFinishPromptSnapshot]
  );

  // -------------------------------------------------------------------------
  // 14) Navegación entre preguntas
  // -------------------------------------------------------------------------
  const goToQuestion = useCallback(
    (targetIndex, { stateOverrides = {}, animateDirection = 'forward', delay = 260, force = false } = {}) => {
      const total = filteredQuestions.length;
      if (total === 0) return;
      const boundedIndex = Math.min(Math.max(targetIndex, 0), total - 1);
      if (!force && boundedIndex === currentQuestion) return;

      if (animateDirection !== 'none') {
        setAnimateQuestion(false);
      }

      const executeTransition = () => {
        if (simulationMode) {
          const elapsed = Math.round((Date.now() - questionStartTime) / 1000);
          setQuestionTimes(prev => ({ ...prev, [currentQuestion]: (prev[currentQuestion] || 0) + elapsed }));
        }
        setCurrentQuestion(boundedIndex);
        setQuestionStartTime(Date.now());
        setAttemptedIncorrect([]);
        setSelectedAnswer(null);
        setShowExplanation(false);

        // Callback para tracking de Práctica Continua
        if (onQuestionStart) {
          onQuestionStart();
        }

        if (shuffledQuestions.length > 0) {
          const newState = {
            quizId,
            currentQuestion: boundedIndex,
            userAnswers: stateOverrides.userAnswers ?? userAnswers,
            correctAnswersCount: stateOverrides.correctAnswersCount ?? correctAnswersCount,
            incorrectAnswersCount: stateOverrides.incorrectAnswersCount ?? incorrectAnswersCount,
            streak: stateOverrides.streak ?? streak,
            incorrectQuestions: stateOverrides.incorrectQuestions ?? incorrectQuestions,
            attemptedIncorrect: [],
            questionsAnswered: stateOverrides.questionsAnswered ?? questionsAnswered,
            totalTimeSpent: stateOverrides.totalTimeSpent ?? totalTimeSpent,
            selectedYear,
            selectedSubtopic,
            timeLeft: stateOverrides.timeLeft ?? timeLeft,
            showResults: false,
            selectedAnswer: null,
            showExplanation: false,
            isPaused,
            totalPausedTime,
            simulationMode,
            simulationTime,
            title,
            simulacroId,
            questionOrder: shuffledQuestions.map((q) => q.id),
            timestamp: Date.now(),
          };
          saveStateImmediate(newState);
        }

        if (animateDirection !== 'none') {
          setAnimateQuestion(true);
        }
      };

      if (animateDirection !== 'none') {
        setTimeout(executeTransition, delay);
      } else {
        executeTransition();
      }
    },
    [
      filteredQuestions,
      currentQuestion,
      setCurrentQuestion,
      setQuestionStartTime,
      setAttemptedIncorrect,
      setSelectedAnswer,
      setShowExplanation,
      shuffledQuestions,
      quizId,
      userAnswers,
      correctAnswersCount,
      incorrectAnswersCount,
      streak,
      incorrectQuestions,
      questionsAnswered,
      totalTimeSpent,
      selectedYear,
      selectedSubtopic,
      timeLeft,
      isPaused,
      totalPausedTime,
      simulationMode,
      simulationTime,
      title,
      saveStateImmediate,
      onQuestionStart,
    ]
  );

  const setAnswerByOptionId = useCallback(
    (optionId, { skipRipple = false } = {}) => {
      if (!currentQuestionData) return false;
      if (optionId === null || optionId === undefined) return false;
      if (simulationMode && isPaused) return false;

      const optionExists = currentQuestionOptions.some((opt) => opt?.id === optionId);
      if (!optionExists) return false;

      // Toggle: si hace click en la misma opción ya seleccionada, desmarcar
      if (optionId === (userAnswers[currentQuestion] ?? null)) {
        setUserAnswers((prev) => {
          const next = [...prev];
          next[currentQuestion] = null;
          const totalRespondidas = next.filter((v) => v !== null && v !== undefined).length;
          setQuestionsAnswered(totalRespondidas);
          persistQuizState({ userAnswers: next, questionsAnswered: totalRespondidas });
          return next;
        });
        return true;
      }

      let nextUserAnswers = null;
      setUserAnswers((prev) => {
        const next = [...prev];
        next[currentQuestion] = optionId;
        const totalRespondidas = next.filter((value) => value !== null && value !== undefined).length;
        setQuestionsAnswered(totalRespondidas);
        persistQuizState({ userAnswers: next, questionsAnswered: totalRespondidas });
        nextUserAnswers = next;
        return next;
      });

      if (!skipRipple) {
        setRippleOptionId(optionId);
        setTimeout(() => setRippleOptionId(null), 600);
      }

      if (simulationMode && !showResults) {
        const snapshot = computeSimulationSnapshot(nextUserAnswers || userAnswers, { includeIncorrectList: true });
        setIncorrectQuestions(snapshot.incorrectQuestions);

        if (autoAdvanceTimeoutRef.current) {
          clearTimeout(autoAdvanceTimeoutRef.current);
        }

        const advanceDelay = skipRipple ? 140 : 220;
        const isLastQuestion = currentQuestion >= filteredQuestions.length - 1;

        if (isLastQuestion) {
          autoAdvanceTimeoutRef.current = setTimeout(() => {
            autoAdvanceTimeoutRef.current = null;
            openFinishPrompt(snapshot);
          }, advanceDelay);
        } else if (!isPaused) {
          const overrides = {
            userAnswers: nextUserAnswers || userAnswers,
            questionsAnswered: snapshot.answered,
            incorrectQuestions: snapshot.incorrectQuestions,
          };
          autoAdvanceTimeoutRef.current = setTimeout(() => {
            autoAdvanceTimeoutRef.current = null;
            goToQuestion(currentQuestion + 1, {
              stateOverrides: overrides,
            });
          }, advanceDelay);
        }
      }

      return true;
    },
    [
      currentQuestionData,
      currentQuestionOptions,
      currentQuestion,
      persistQuizState,
      simulationMode,
      isPaused,
      showResults,
      computeSimulationSnapshot,
      userAnswers,
      setIncorrectQuestions,
      filteredQuestions,
      openFinishPrompt,
      goToQuestion,
    ]
  );

  const clearCurrentAnswer = useCallback(() => {
    if (simulationMode && isPaused) return;
    setUserAnswers((prev) => {
      if (!prev || (prev[currentQuestion] === null || prev[currentQuestion] === undefined)) {
        return prev;
      }
      const next = [...prev];
      next[currentQuestion] = null;
      const totalRespondidas = next.filter((value) => value !== null && value !== undefined).length;
      setQuestionsAnswered(totalRespondidas);
      persistQuizState({ userAnswers: next, questionsAnswered: totalRespondidas });
      return next;
    });
  }, [currentQuestion, persistQuizState, simulationMode, isPaused]);

  const clearAllQuizSavedStates = useCallback(() => {
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('mediexampro_quiz_state_')) keysToRemove.push(key);
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
      try {
        window.dispatchEvent(new CustomEvent('mediexam_quiz_state', { detail: { clearedAll: true } }));
      } catch (_) {}
    } catch (_) {}
  }, []);

  const finalizarSimulacro = useCallback((finalErrors) => {
    if (finalErrors && finalErrors.length > 0) {
      addBulkSimulationErrors(finalErrors);
    }

    // Capturar tiempo de la pregunta actual antes de finalizar
    if (simulationMode) {
      const elapsed = Math.round((Date.now() - questionStartTime) / 1000);
      setQuestionTimes(prev => ({ ...prev, [currentQuestion]: (prev[currentQuestion] || 0) + elapsed }));
    }

    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    cancelAutosave();
    setIsPaused(false);
    setShowPauseOverlay(false);
    setTimeLeft(0);

    try {
      const finalState = {
        quizId,
        currentQuestion,
        selectedAnswer,
        showExplanation,
        userAnswers,
        incorrectQuestions: finalErrors || incorrectQuestions,
        attemptedIncorrect,
        streak,
        correctAnswersCount,
        incorrectAnswersCount,
        timeLeft: 0,
        showResults: true,
        questionsAnswered,
        selectedYear,
        selectedSubtopic,
        isPaused: false,
        totalPausedTime,
        simulationMode,
        simulationTime,
        title,
        simulacroId,
        questionOrder: (shuffledQuestions || []).map((q) => q.id),
        timestamp: Date.now(),
      };
      saveStateImmediate(finalState);
    } catch (_) {}

    setShowResults(true);
    clearSavedState();
    clearAllQuizSavedStates();
    setFinishPromptSnapshot(null);
  }, [
    addBulkSimulationErrors,
    attemptedIncorrect,
    cancelAutosave,
    clearAllQuizSavedStates,
    clearSavedState,
    correctAnswersCount,
    currentQuestion,
    incorrectAnswersCount,
    incorrectQuestions,
    quizId,
    saveStateImmediate,
    selectedAnswer,
    selectedSubtopic,
    selectedYear,
    showExplanation,
    shuffledQuestions,
    simulationMode,
    simulationTime,
    streak,
    timeLeft,
    title,
    totalPausedTime,
    userAnswers,
    questionsAnswered,
    questionStartTime,
  ]);

  const dismissFinishPrompt = useCallback(() => {
    setFinishPromptSnapshot(null);
  }, []);

  // Guardar progreso del simulacro en MySQL sin finalizar
  // silent=true para auto-save (sin mensajes en pantalla)
  const saveProgress = useCallback(async (silent = false) => {
    if (!simulationMode || showResults) return;
    if (isSavingRef.current) return; // prevenir saves concurrentes
    isSavingRef.current = true;

    // Capturar tiempo de la pregunta actual
    const elapsed = Math.round((Date.now() - questionStartTime) / 1000);
    const currentTimes = { ...questionTimes, [currentQuestion]: (questionTimes[currentQuestion] || 0) + elapsed };

    // Construir answers array desde el estado actual
    // Usa la misma lógica de búsqueda que computeExamAnalytics (4 fallbacks)
    const answers = filteredQuestions.map((q, i) => {
      const answerId = userAnswers[i] ?? null;
      const options = Array.isArray(q.options) ? q.options : [];

      // Buscar opción seleccionada (mismos fallbacks que examAnalytics)
      let selectedOpt = null;
      if (answerId != null) {
        selectedOpt = options.find(opt => opt && opt.id === answerId)
          || options.find(opt => opt && (
            String(opt.option_letter).toLowerCase() === String(answerId).toLowerCase() ||
            String(opt.option_label).toLowerCase() === String(answerId).toLowerCase()
          ));
        if (!selectedOpt && typeof answerId === 'number' && options[answerId]) {
          selectedOpt = options[answerId];
        }
      }

      // Buscar opción correcta (correct, is_correct — ambos nombres)
      const correctOpt = options.find(opt => opt && (opt.correct || opt.is_correct));

      let status = 'skipped';
      if (answerId != null) {
        status = (selectedOpt && (selectedOpt.correct || selectedOpt.is_correct)) ? 'correct' : 'incorrect';
      }
      const noteData = questionNotes[i];
      return {
        questionId: q.id || null,
        questionIndex: i,
        questionText: q.question_text || null,
        caseDescription: q.case_description || null,
        area: q.area || null,
        topic: q.topic || null,
        subtopic: q.subtopic || null,
        subtopicSpecific: q.subtopic_specific || null,
        organsystem: q.organsystem || null,
        especialidad: q.especialidad || null,
        year: q.year || null,
        userAnswerLabel: selectedOpt?.option_letter || selectedOpt?.option_label || null,
        userAnswerText: selectedOpt?.option_text || null,
        correctAnswerLabel: correctOpt?.option_letter || correctOpt?.option_label || null,
        correctAnswerText: correctOpt?.option_text || null,
        status,
        flagged: markedForReview[i] || false,
        note: noteData?.text || null,
        confidence: noteData?.confidence ?? null,
        timeSpentSeconds: currentTimes[i] ?? null,
      };
    });

    const answered = answers.filter(a => a.status !== 'skipped').length;
    const correct = answers.filter(a => a.status === 'correct').length;
    const incorrect = answers.filter(a => a.status === 'incorrect').length;
    const skipped = answers.length - answered;
    const accuracy = answered > 0 ? Math.round((correct / answered) * 100) : 0;

    const payload = {
      examTitle: title || 'Examen sin Título',
      examType: 'simulacro',
      simulacroId: simulacroId || null,
      totalQuestions: filteredQuestions.length,
      correct, incorrect, skipped, accuracy,
      timeSpentSeconds: timeSpentSeconds || null,
      timeAllocatedSeconds: totalAllocatedSeconds || null,
      startedAt: examStartedAtRef.current,
      currentQuestion,
      status: 'in_progress',
      answers,
    };

    const apiUrl = config.getApiUrl();
    if (!silent) setSaveProgressMsg('Guardando...');

    try {
      let res;
      if (savedSessionId) {
        res = await fetch(`${apiUrl}/exam-sessions/${savedSessionId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${apiUrl}/exam-sessions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      const data = await res.json();
      if (data.success || data.sessionId) {
        if (!savedSessionId && data.sessionId) {
          setSavedSessionId(data.sessionId);
        }
        if (!silent) setSaveProgressMsg('Guardado');
        console.log(`[AutoSave] ${silent ? 'Auto-' : ''}guardado (session ${savedSessionId || data.sessionId}, pregunta ${currentQuestion + 1}/${filteredQuestions.length})`);
      } else {
        if (!silent) setSaveProgressMsg('Error al guardar');
        console.error('[SaveProgress] Error:', data.error);
      }
    } catch (err) {
      if (!silent) setSaveProgressMsg('Error de conexión');
      console.error('[SaveProgress] Fetch error:', err);
    } finally {
      isSavingRef.current = false;
    }

    if (!silent) setTimeout(() => setSaveProgressMsg(null), 2500);
  }, [
    simulationMode, showResults, questionStartTime, questionTimes,
    currentQuestion, filteredQuestions, userAnswers, questionNotes,
    markedForReview, title, simulacroId, timeSpentSeconds,
    totalAllocatedSeconds, savedSessionId,
  ]);

  // -------------------------------------------------------------------------
  // AUTO-SAVE MySQL: debounce al cambiar pregunta + interval de backup
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!simulationMode || showResults || !hasRestored) return;
    if (autoSaveDebounceRef.current) clearTimeout(autoSaveDebounceRef.current);
    autoSaveDebounceRef.current = setTimeout(() => {
      saveProgress(true);
    }, 30000);
    return () => {
      if (autoSaveDebounceRef.current) clearTimeout(autoSaveDebounceRef.current);
    };
  }, [currentQuestion, simulationMode, showResults, hasRestored, saveProgress]);

  useEffect(() => {
    if (!simulationMode || showResults || !hasRestored) return;
    autoSaveIntervalRef.current = setInterval(() => {
      saveProgress(true);
    }, 5 * 60 * 1000);
    return () => {
      if (autoSaveIntervalRef.current) clearInterval(autoSaveIntervalRef.current);
    };
  }, [simulationMode, showResults, hasRestored, saveProgress]);

  // Descartar simulacro sin guardar resultados
  const descartarSimulacro = useCallback(async () => {
    if (!window.confirm('¿Descartar simulacro?\n\nNo se guardarán resultados ni en el historial ni en la base de datos. Esta acción no se puede deshacer.')) {
      return;
    }
    // Limpiar timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    cancelAutosave();

    // Eliminar sesión en BD si ya se creó (el autosave la pudo haber persistido).
    // Sin esto, la sesión queda huérfana en MySQL con status='in_progress' y al
    // recargar aparece como "reanudable" → bug reportado por usuario.
    if (savedSessionId) {
      try {
        const apiUrl = config.getApiUrl();
        await fetch(`${apiUrl}/exam-sessions/${savedSessionId}`, { method: 'DELETE' });
        console.log(`[descartar] sesión ${savedSessionId} eliminada de BD`);
      } catch (err) {
        console.error('[descartar] fallo DELETE en BD (no bloqueante):', err);
        // No bloquear el flow — igual limpiamos local y volvemos al menú
      }
    }

    // Limpiar estado guardado del quiz en localStorage
    clearSavedState();
    clearAllQuizSavedStates();
    // Volver al generador sin pasar por finalizarSimulacro (no guarda en MySQL)
    if (onBack) onBack();
  }, [cancelAutosave, clearSavedState, clearAllQuizSavedStates, onBack, savedSessionId]);

  const finalizeFromPrompt = useCallback(() => {
    if (!finishPromptSnapshot) return;
    finalizarSimulacro(finishPromptSnapshot.incorrectQuestions);
    setFinishPromptSnapshot(null);
  }, [finishPromptSnapshot, finalizarSimulacro]);

  const handleFinalizeConfirm = useCallback(() => {
    if (simulationMode) {
      const snapshot = computeSimulationSnapshot(undefined, { includeIncorrectList: true });
      openFinishPrompt(snapshot);
      return;
    }
    if (window.confirm('¿Deseas finalizar el simulacro? Se guardará tu progreso y verás los resultados.')) {
      finalizarSimulacro(incorrectQuestions);
    }
  }, [simulationMode, computeSimulationSnapshot, openFinishPrompt, finalizarSimulacro, incorrectQuestions]);

  // -------------------------------------------------------------------------
  //  PAUSA AUTOMÁTICA Y ATAJOS DE TECLADO
  // -------------------------------------------------------------------------
  // Atajos:
  //  - Barra espaciadora: alterna Pausa/Reanudar
  //  - Escape: Pausa inmediata
  //  - F: Finalizar simulacro (con confirmación)
  //  - P: Abrir/cerrar paleta de preguntas
  //  - M: Marcar o desmarcar para revisión
  //  - Flechas ←/→: navegar entre preguntas
  //  - A–E o 1–5: seleccionar opción, Backspace/Delete: limpiar selección
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!simulationMode || showResults) return;
      if (e.repeat) return; // evitar repetidos al mantener presionado

      const ae = document.activeElement;
      const tag = ae?.tagName;
      const editing = ae?.isContentEditable;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || editing) return;

      const key = e.key?.toLowerCase();
      const code = e.code;

      if (finishPromptSnapshot) {
        if (key === 'enter') {
          e.preventDefault();
          finalizeFromPrompt();
        } else if (key === 'escape') {
          e.preventDefault();
          dismissFinishPrompt();
        }
        return;
      }

      if (code === 'Space' || key === ' ') {
        e.preventDefault();
        if (isPaused) {
          handleResume();
        } else {
          handlePause();
        }
        return;
      }

      if (key === 'escape') {
        e.preventDefault();
        if (!isPaused) handlePause();
        return;
      }

      if (key === 'f') {
        e.preventDefault();
        handleFinalizeConfirm();
        return;
      }

      if (key === 'p') {
        e.preventDefault();
        if (!isPaused) {
          setShowPalette((prev) => !prev);
        }
        return;
      }

      if (!isPaused && (code === 'ArrowLeft' || key === 'arrowleft')) {
        e.preventDefault();
        setCurrentQuestion((q) => Math.max(0, q - 1));
        return;
      }

      if (!isPaused && (code === 'ArrowRight' || key === 'arrowright')) {
        e.preventDefault();
        setCurrentQuestion((q) => Math.min(filteredQuestions.length - 1, q + 1));
        return;
      }

      if (!isPaused && key === 'm') {
        e.preventDefault();
        setMarkedForReview((prev) => {
          const next = { ...(prev || {}) };
          next[currentQuestion] = !next[currentQuestion];
          return next;
        });
        return;
      }

      if (!isPaused && key === 'n') {
        e.preventDefault();
        setShowNotePanel((prev) => !prev);
        return;
      }

      if (!isPaused && (key === 'backspace' || key === 'delete')) {
        e.preventDefault();
        clearCurrentAnswer();
        return;
      }

      if (!isPaused) {
        const alpha = ['a', 'b', 'c', 'd', 'e'];
        const digit = ['1', '2', '3', '4', '5'];
        let optionId = null;

        if (alpha.includes(key)) {
          const label = key.toUpperCase();
          const match = currentQuestionOptions.find(
            (opt) => (opt?.option_label || '').toUpperCase() === label
          );
          optionId = match?.id ?? null;
        } else if (digit.includes(key)) {
          const index = parseInt(key, 10) - 1;
          if (index >= 0 && index < orderedOptionsByLabel.length) {
            optionId = orderedOptionsByLabel[index]?.id ?? null;
          }
        }

        if (optionId) {
          e.preventDefault();
          setAnswerByOptionId(optionId, { skipRipple: true });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    simulationMode,
    showResults,
    isPaused,
    handlePause,
    handleResume,
    setCurrentQuestion,
    filteredQuestions.length,
    setMarkedForReview,
    setShowNotePanel,
    currentQuestion,
    setShowPalette,
    clearCurrentAnswer,
    currentQuestionOptions,
    orderedOptionsByLabel,
    setAnswerByOptionId,
    handleFinalizeConfirm,
    finishPromptSnapshot,
    finalizeFromPrompt,
    dismissFinishPrompt,
  ]);

  // Navegación con flechas en modo estudio
  useEffect(() => {
    if (simulationMode || showResults) return;

    const handleStudyArrows = (e) => {
      if (e.repeat) return;

      const ae = document.activeElement;
      const tag = ae?.tagName;
      const editing = ae?.isContentEditable;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || editing) return;

      const key = e.key?.toLowerCase();
      const code = e.code;

      if (code === 'ArrowLeft' || key === 'arrowleft') {
        e.preventDefault();
        setCurrentQuestion((q) => Math.max(0, q - 1));
      } else if (code === 'ArrowRight' || key === 'arrowright') {
        e.preventDefault();
        setCurrentQuestion((q) => Math.min(filteredQuestions.length - 1, q + 1));
      }
    };

    window.addEventListener('keydown', handleStudyArrows);
    return () => window.removeEventListener('keydown', handleStudyArrows);
  }, [simulationMode, showResults, filteredQuestions.length, setCurrentQuestion]);

  // Auto-pausa al ocultar pestaña o al perder foco (minimizar/cambiar ventana)
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden && simulationMode && !showResults && !isPaused) {
        handlePause();
      }
    };
    const onBlur = () => {
      if (simulationMode && !showResults && !isPaused) {
        handlePause();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', onBlur);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', onBlur);
    };
  }, [simulationMode, showResults, isPaused, handlePause]);

  const pauseLifecycleStateRef = useRef({ simulationMode, showResults });
  const pauseHandlerRef = useRef(handlePause);

  useEffect(() => {
    pauseLifecycleStateRef.current = { simulationMode, showResults };
    pauseHandlerRef.current = handlePause;
  }, [simulationMode, showResults, handlePause]);

  useEffect(() => {
    return () => {
      const { simulationMode: sim, showResults: results } = pauseLifecycleStateRef.current;
      if (sim && !results) {
        pauseHandlerRef.current({ showOverlay: false });
      }
    };
  }, []);

  // -------------------------------------------------------------------------
  // --- MAPEOS PARA TABLA Y ESQUEMA DE "Carcinoma de Tiroides" --------------
  // -------------------------------------------------------------------------
  //  A) Mapeo para la tabla:
  const subtopicTableMap = {
    'Carcinoma de Tiroides': <CarcinomadeTiroidesTable />,
    'Nódulo tiroideo': <NoduloTiroideoTable />
    // Agrega otras entradas si lo deseas
  };

  //  B) Mapeo para el esquema:
  const subtopicEsquemaMap = {
    'Carcinoma de Tiroides': <CarcinomadeTiroidesEsquema />,
    'Nódulo tiroideo': <NoduloTiroideoEsquema />
    // Agrega otros subtemas y sus esquemas
  };

  // Determinar si esta pregunta tiene tabla/esquema disponible
  const TableComponent =
    currentQuestionData && subtopicTableMap[currentQuestionData.subtopic]
      ? subtopicTableMap[currentQuestionData.subtopic]
      : null;

  const EsquemaComponent =
    currentQuestionData && subtopicEsquemaMap[currentQuestionData.subtopic]
      ? subtopicEsquemaMap[currentQuestionData.subtopic]
      : null;

  // Estados para mostrar/ocultar
  const [showSubtopicTable, setShowSubtopicTable] = useState(false);
  const [showSubtopicEsquema, setShowSubtopicEsquema] = useState(false);

  // Actualizar preguntas si cambian (pero mantener el orden si ya se restauró)
  useEffect(() => {
    // Solo actualizar si no tenemos preguntas pero las recibimos
    if (shuffledQuestions.length === 0 && questions.length > 0 && !hasInitializedQuestions) {
      console.log('🎲 Inicializando preguntas disponibles');
      // Solo aleatorizar si es simulationMode Y NO se quiere preservar el orden
      if (simulationMode && !preserveOrder) {
        const shuffled = [...questions].sort(() => Math.random() - 0.5);
        setShuffledQuestions(shuffled);
      } else {
        setShuffledQuestions(questions);
      }
      setHasInitializedQuestions(true);
    }
  }, [questions.length, simulationMode, preserveOrder]); // Dependencias simplificadas

  // NOTA: Este useEffect fue eliminado porque estaba duplicado y causaba el error
  // "Cannot access before initialization". El useEffect de auto-guardado
  // ahora está después de todas las declaraciones de estado (línea ~788)

  // -------------------------------------------------------------------------
  // 12) Resetea selectedAnswer y Explicación al cambiar de pregunta
  // -------------------------------------------------------------------------
  useEffect(() => {
    setSelectedAnswer(null);
    setShowExplanation(false);
    setShowDecisionTable(false);
    setAttemptedIncorrect([]); // Reset incorrect attempts
    setCelebrationOptionId(null); // Reset celebration

    // Asegurarnos de ocultar la tabla y el esquema al cambiar de pregunta
    setShowSubtopicTable(false);
    setShowSubtopicEsquema(false);
  }, [currentQuestionData]);

  // -------------------------------------------------------------------------
  // 13) Manejo de la respuesta
  // -------------------------------------------------------------------------
  const handleAnswer = (optionId) => {
    if (!currentQuestionData) return;
    if (simulationMode && isPaused) return;
    
    // Si ya seleccionó la correcta, no permitir más clicks
    if (showExplanation) return;
    
    // Si ya intentó esta opción incorrecta, no hacer nada
    if (attemptedIncorrect.includes(optionId)) return;
    
    // Verificar límites de preguntas antes de permitir responder
    const currentQuestionNumber = questionsAnswered + 1;
    if (!canAnswerQuestion(currentQuestionNumber)) {
      if (!user) {
        // Usuario no logueado, límite de 5 preguntas
        setUpgradeMessage(showUpgradePrompt('loginRequired'));
        setShowLoginModal(true);
      } else if (subscription.plan === 'free') {
        // Usuario gratuito, límite de 15 preguntas
        setUpgradeMessage(showUpgradePrompt('upgradeRequired'));
        setShowUpgradeModal(true);
      }
      return; // No permitir responder más preguntas
    }
    
    // Activar efecto ripple
    setRippleOptionId(optionId);
    setTimeout(() => setRippleOptionId(null), 600);
    
    const option = currentQuestionData.options.find((opt) => opt.id === optionId);
    const isCorrect = option?.correct || false;

    // Guardar la respuesta en userAnswers
    setUserAnswers((prev) => {
      const updated = [...prev];
      updated[currentQuestion] = optionId;
      return updated;
    });

    // MODO ESTUDIO → comportamiento diferente según si es correcta o no
    if (!simulationMode) {
      if (isCorrect) {
        // Respuesta correcta: mostrar todo
        updateErrorBank(currentQuestionData, true, false);
        setSelectedAnswer(optionId);
        setShowExplanation(true);
        
        // Actualizar Estadísticas visuales
        setCorrectAnswersCount(prev => prev + 1);
        setStreak(prev => prev + 1);
        
        // Incrementar contador de preguntas respondidas
        setQuestionsAnswered(prev => prev + 1);
        
        // Guardar estado inmediatamente después de respuesta correcta
        if (shuffledQuestions.length > 0) {
          const newState = {
            quizId,
            currentQuestion,
            userAnswers: simulationMode ? [...userAnswers, optionId] : userAnswers,
            correctAnswersCount: correctAnswersCount + 1,
            incorrectAnswersCount,
            streak: streak + 1,
            incorrectQuestions,
            attemptedIncorrect,
            questionsAnswered: questionsAnswered + 1,
            totalTimeSpent,
            selectedYear,
            selectedSubtopic,
            timeLeft,
            isPaused,
            totalPausedTime,
            simulationMode,
            simulationTime,
            title,
            simulacroId,
            questionOrder: shuffledQuestions.map(q => q.id),
            timestamp: Date.now()
          };
          saveStateImmediate(newState);
        }
        
        // Rastrear pregunta respondida en la base de datos
        trackQuestionAnswered();
        
        // Mostrar celebración en la opción correcta
        setCelebrationOptionId(optionId);
        setTimeout(() => setCelebrationOptionId(null), 2000);
        
        // Calcular tiempo de respuesta
        const timeSpent = Math.round((Date.now() - questionStartTime) / 1000);
        setTotalTimeSpent(prev => prev + timeSpent);

        // Callback para tracking de Práctica Continua (modo estudio - respuesta correcta)
        if (onQuestionAnswered) {
          onQuestionAnswered(currentQuestionData, option, true);
        }
      } else {
        // Respuesta incorrecta: solo agregar a intentados, no mostrar Explicación general
        setAttemptedIncorrect(prev => [...prev, optionId]);
        updateErrorBank(currentQuestionData, false, false);
        
        // Si es el primer intento incorrecto, actualizar Estadísticas
        if (attemptedIncorrect.length === 0) {
          setIncorrectAnswersCount(prev => prev + 1);
          setStreak(0); // Reset streak
          
          // Guardar estado inmediatamente después de respuesta incorrecta
          if (shuffledQuestions.length > 0) {
            const newState = {
              quizId,
              currentQuestion,
              userAnswers,
              correctAnswersCount,
              incorrectAnswersCount: incorrectAnswersCount + 1,
              streak: 0,
              incorrectQuestions,
              attemptedIncorrect: [...attemptedIncorrect, optionId],
              questionsAnswered,
              totalTimeSpent,
              selectedYear,
              selectedSubtopic,
              timeLeft,
              isPaused,
              totalPausedTime,
              simulationMode,
              simulationTime,
              title,
              simulacroId,
              questionOrder: shuffledQuestions.map(q => q.id),
              timestamp: Date.now()
            };
            saveStateImmediate(newState);
          }
        }
      }

      // Actualizar studyHistory
      const qId = String(currentQuestionData.id);
      const oldQ = studyHistory.questionHistory[qId] || { attempts: 0, correct: 0 };

      setStudyHistory((prev) => ({
        ...prev,
        attempts: prev.attempts + 1,
        correct: prev.correct + (isCorrect ? 1 : 0),
        questionHistory: {
          ...prev.questionHistory,
          [qId]: {
            attempts: oldQ.attempts + 1,
            correct: oldQ.correct + (isCorrect ? 1 : 0),
            lastAttempt: new Date().toISOString()
          }
        }
      }));

      if (!isCorrect) {
        setIncorrectQuestions((prev) => [...prev, currentQuestionData]);
      }
    }
    // MODO SIMULACRO → NO se actualiza ErrorBank aquí, sino al final
    else {
      const qId = String(currentQuestionData.id);
      const oldQ = studyHistory.questionHistory[qId] || { attempts: 0, correct: 0 };

      // Actualiza solo el historial general
      setStudyHistory((prev) => ({
        ...prev,
        attempts: prev.attempts + 1,
        correct: prev.correct + (isCorrect ? 1 : 0),
        questionHistory: {
          ...prev.questionHistory,
          [qId]: {
            attempts: oldQ.attempts + 1,
            correct: oldQ.correct + (isCorrect ? 1 : 0),
            lastAttempt: new Date().toISOString()
          }
        }
      }));

      // Si es incorrecta, la agregamos a la lista
      let newIncorrect = incorrectQuestions;
      if (!isCorrect) {
        newIncorrect = [...incorrectQuestions, currentQuestionData];
        setIncorrectQuestions(newIncorrect);
      }

      // Callback para tracking de Práctica Continua (modo simulacro)
      if (onQuestionAnswered) {
        onQuestionAnswered(currentQuestionData, option, isCorrect);
      }

      // Si estamos en la última pregunta, finalizar
      if (currentQuestion === filteredQuestions.length - 1) {
        finalizarSimulacro(newIncorrect);
      } else {
        nextQuestion();
      }
    }
  };

  const nextQuestion = () => {
    if (filteredQuestions.length === 0) return;
    const nextIndex = (currentQuestion + 1) % filteredQuestions.length;
    const forceTransition = filteredQuestions.length === 1;
    goToQuestion(nextIndex, { force: forceTransition });
  };

  // ===========================================================================
  // INTERFAZ "CLINICAL EDITORIAL — READING ROOM"
  // Mismo sistema que GenerateExam, mood distinto:
  // composición lineal, pregunta como hero, dopamina sofisticada, mínimo chrome
  // ===========================================================================
  const T = darkMode
    ? {
        paper: '#07090D',
        paperSoft: '#0F131A',
        paperWarm: '#181D27',
        paperLift: '#1F2533',
        ink: '#F2EDE0',           // headers, números, timers (alto luminance)
        inkBody: '#DCD2BC',       // body text de lectura larga (cream cálido cómodo)
        inkSoft: '#B5AC9A',
        inkMute: '#6A6356',
        hairline: '#2B3140',
        hairlineSoft: '#1A1F2A',
        accent: '#7DA5D9',         // midnight blue (selected state)
        accentDeep: '#4F7AB8',
        accentSoft: '#0F1A2E',
        accentTint: '#0A1424',
        // sage = AQUA / CYAN VIVO PREMIUM (estilo gráficos Nature/Lancet)
        sage: '#5CDCEF',           // cyan-aqua luminoso lit-from-within (alto chroma)
        sageDeep: '#2BB5CB',       // aqua profundo para hover
        sageSoft: '#0B2630',       // tinted bg para banners de éxito
        sageGlow: 'rgba(92, 220, 239, 0.32)',
        // crimson = CINABRIO / CORAL VIVO (rojo histórico editorial, alto impacto)
        crimson: '#F2604D',        // coral-vermillion luminoso vivo
        crimsonDeep: '#C04030',    // cinabrio profundo para hover
        crimsonSoft: '#2C140F',    // tinted bg
        crimsonGlow: 'rgba(242, 96, 77, 0.32)',
        glow: 'rgba(125, 165, 217, 0.22)',
      }
    : {
        paper: '#F4EFE3',
        paperSoft: '#FBF8F1',
        paperWarm: '#EDE5D2',
        paperLift: '#E5DCC4',
        ink: '#0E1218',
        inkBody: '#0E1218',
        inkSoft: '#3E4654',
        inkMute: '#7A8499',
        hairline: '#D8D2C5',
        hairlineSoft: '#E5DFD2',
        accent: '#1E3A6F',         // midnight blue Cambridge
        accentDeep: '#0F2147',
        accentSoft: '#E8EEF7',
        accentTint: '#F2F5FB',
        // sage = CYAN-AQUA VIVO premium (más saturado, más impactante)
        sage: '#0596AC',           // cyan-teal vivo, alto chroma editorial
        sageDeep: '#03788A',
        sageSoft: '#D5F0F4',
        sageGlow: 'rgba(5, 150, 172, 0.22)',
        // crimson = CINABRIO CHINO vivo (rojo escarlata editorial premium)
        crimson: '#D8311C',        // cinabrio escarlata vivo
        crimsonDeep: '#A82210',
        crimsonSoft: '#FCE4DD',
        crimsonGlow: 'rgba(216, 49, 28, 0.20)',
        glow: 'rgba(30, 58, 111, 0.16)',
      };

  const finishPromptStats = finishPromptSnapshot
    ? {
        total: finishPromptSnapshot.total ?? 0,
        answered: finishPromptSnapshot.answered ?? 0,
        correct: finishPromptSnapshot.correct ?? 0,
        incorrect: finishPromptSnapshot.incorrect ?? 0,
        skipped: finishPromptSnapshot.skipped ?? 0,
        accuracy: finishPromptSnapshot.accuracy ?? 0,
      }
    : null;

  const finishPromptOverlay = (
    <AnimatePresence>
      {finishPromptSnapshot && finishPromptStats && (
        <>
          <motion.div
            key="finish-backdrop"
            className={`fixed inset-0 z-[90] ${darkMode ? 'bg-slate-950/80 backdrop-blur-xl' : 'bg-slate-900/55 backdrop-blur-lg'}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          />
          <motion.div
            key="finish-dialog"
            className="fixed inset-0 z-[95] flex items-center justify-center px-4"
            initial={{ opacity: 0, scale: 0.9, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 24 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            onClick={dismissFinishPrompt}
          >
            <div
              className={`w-full max-w-lg rounded-3xl border px-8 py-7 shadow-2xl ${
                darkMode
                  ? 'bg-slate-950/90 border-cyan-500/40 text-gray-100'
                  : 'bg-white/95 backdrop-blur border-slate-200 text-slate-800'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className={`text-xl font-bold ${darkMode ? 'text-cyan-300' : 'text-slate-800'}`}>
                ¿Enviar tus respuestas?
              </h3>
              <p className={`mt-2 text-sm ${darkMode ? 'text-gray-300/85' : 'text-slate-600'}`}>
                Has respondido {finishPromptStats.answered}/{finishPromptStats.total} preguntas.
                {finishPromptStats.skipped > 0
                  ? ` Te quedan ${finishPromptStats.skipped} por revisar antes de finalizar.`
                  : ' Todas cuentan con una respuesta registrada.'}
              </p>
              <div className={`mt-5 grid grid-cols-2 gap-3 ${darkMode ? 'text-gray-200' : 'text-slate-700'}`}>
                <div className={`${darkMode ? 'bg-slate-900/80 border border-slate-700' : 'bg-slate-50 border border-slate-200'} rounded-2xl p-3`}>
                  Precisión
                  <div className="text-lg font-semibold">{finishPromptStats.accuracy}%</div>
                </div>
                <div className={`${darkMode ? 'bg-slate-900/80 border border-slate-700' : 'bg-slate-50 border border-slate-200'} rounded-2xl p-3`}>
                  Sin responder
                  <div className="text-lg font-semibold">{finishPromptStats.skipped}</div>
                </div>
              </div>
              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  onClick={descartarSimulacro}
                  className={`rounded-2xl border px-4 py-2 text-sm font-semibold ${
                    darkMode
                      ? 'border-gray-600/70 text-gray-400 hover:border-red-500/60 hover:text-red-300 hover:bg-red-950/30'
                      : 'border-gray-200 text-gray-400 hover:border-red-300 hover:text-red-600 hover:bg-red-50'
                  }`}
                >
                  Descartar
                </button>
                <button
                  onClick={dismissFinishPrompt}
                  className={`rounded-2xl border px-4 py-2 text-sm font-semibold ${
                    darkMode
                      ? 'border-slate-600/70 text-slate-200 hover:border-slate-400/80 hover:bg-slate-800'
                      : 'border-slate-200 text-slate-700 hover:border-slate-400 hover:bg-slate-50'
                  }`}
                >
                  Seguir revisando
                </button>
                <button
                  onClick={finalizeFromPrompt}
                  className={`rounded-2xl px-4 py-2 text-sm font-semibold shadow-lg ${
                    darkMode
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-slate-900 hover:from-cyan-400 hover:to-blue-500'
                      : 'bg-gradient-to-r from-sky-500 to-indigo-500 text-white hover:from-sky-400 hover:to-indigo-500'
                  }`}
                >
                  Finalizar simulacro
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );


  // -------------------------------------------------------------------------
  // 15) Reiniciar
  // -------------------------------------------------------------------------
  const resetStudy = () => {
    // Limpiar estado guardado al reiniciar
    clearSavedState();
    
    setStudyHistory({
      attempts: 0,
      correct: 0,
      questionHistory: {}
    });
    setFinishPromptSnapshot(null);
    setIncorrectQuestions([]);
    setCurrentQuestion(0);
    setReviewMode(false);
    setShowResults(false);
    // Reiniciar temporizador y estados de pausa
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    setIsPaused(false);
    setPauseStartTime(null);
    setTotalPausedTime(0);
    setTimeLeft(simulationMode ? simulationTime * 60 : null);
    
    // Reiniciar todos los contadores
    setCorrectAnswersCount(0);
    setIncorrectAnswersCount(0);
    setStreak(0);
    setUserAnswers([]);
    setQuestionsAnswered(0);
    setAttemptedIncorrect([]);
    setTotalTimeSpent(0);
    setSavedHistory(false);
    setShowPalette(false);
    setPaletteOnlyUnanswered(false);
    setMarkedForReview({});
    setExtraTime(0);
    setSelectedYear('Todos');
    setSelectedSubtopic('Todos');
    setSortByYear(false);
    
    // Reinicializar las preguntas con nuevo orden
    setHasInitializedQuestions(false);
    // Solo aleatorizar si es simulationMode Y NO se quiere preservar el orden
    if (simulationMode && !preserveOrder) {
      const shuffled = [...questions].sort(() => Math.random() - 0.5);
      setShuffledQuestions(shuffled);
    } else {
      setShuffledQuestions(questions);
    }
    setHasInitializedQuestions(true);
    setUserAnswers([]);
  };

  // -------------------------------------------------------------------------
  // 15.1) ✨  Descarga en PDF con claves
  // -------------------------------------------------------------------------
  const handleDownloadPDFWithKeys = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const marginX = 40;
    let cursorY = 40;
  
    // Título
    doc.setFontSize(16);
    doc.text(title || 'Examen', marginX, cursorY);
    cursorY += 24;
  
    filteredQuestions.forEach((q, index) => {
      doc.setFontSize(12);
      doc.text(`Pregunta ${index + 1} (${q.year}):`, marginX, cursorY);
      cursorY += 16;

      if (q.case_description) {
        const split = doc.splitTextToSize(q.case_description, 515);
        doc.text(split, marginX, cursorY);
        cursorY += split.length * 14;
      }

      const splitQ = doc.splitTextToSize(q.question_text, 515);
      doc.text(splitQ, marginX, cursorY);
      cursorY += splitQ.length * 14 + 6;

      // Opciones sin marcar la correcta
      const optionRows = q.options.map(opt => [
        `${opt.option_label}) ${opt.option_text}`
      ]);

      autoTable(doc, {
        head: [['Alternativas']],
        body: optionRows,
        startY: cursorY,
        styles: { fontSize: 10, cellWidth: 'wrap' },
        headStyles: { fillColor: [63, 81, 181] }
      });
  
      cursorY = doc.lastAutoTable.finalY + 12;

      // Respuesta correcta al final
      const correctOption = q.options.find(opt => opt.correct);
      if (correctOption) {
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text('Respuesta correcta:', marginX, cursorY);
        cursorY += 14;
        doc.setFont(undefined, 'normal');
        const correctText = `${correctOption.option_label}) ${correctOption.option_text}`;
        const splitCorrect = doc.splitTextToSize(correctText, 515);
        doc.text(splitCorrect, marginX, cursorY);
        cursorY += splitCorrect.length * 14 + 20;
      }
  
      if (cursorY > 720) {
        doc.addPage();
        cursorY = 40;
      }
    });
  
    doc.save(`${title || 'examen'}_con_claves.pdf`);
  };

  // -------------------------------------------------------------------------
  // 15.2) ✨ Descarga en PDF sin claves
  // -------------------------------------------------------------------------
  const handleDownloadPDFWithoutKeys = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const marginX = 40;
    let cursorY = 40;
  
    // Título
    doc.setFontSize(16);
    doc.text(title || 'Examen', marginX, cursorY);
    cursorY += 24;
  
    filteredQuestions.forEach((q, index) => {
      doc.setFontSize(12);
      doc.text(`Pregunta ${index + 1} (${q.year}):`, marginX, cursorY);
      cursorY += 16;

      if (q.case_description) {
        const split = doc.splitTextToSize(q.case_description, 515);
        doc.text(split, marginX, cursorY);
        cursorY += split.length * 14;
      }

      const splitQ = doc.splitTextToSize(q.question_text, 515);
      doc.text(splitQ, marginX, cursorY);
      cursorY += splitQ.length * 14 + 6;

      // Opciones sin marcar la correcta
      const optionRows = q.options.map(opt => [
        `${opt.option_label}) ${opt.option_text}`
      ]);

      autoTable(doc, {
        head: [['Alternativas']],
        body: optionRows,
        startY: cursorY,
        styles: { fontSize: 10, cellWidth: 'wrap' },
        headStyles: { fillColor: [63, 81, 181] }
      });

      cursorY = doc.lastAutoTable.finalY + 20;

      if (cursorY > 720) {
        doc.addPage();
        cursorY = 40;
      }
    });

    doc.save(`${title || 'examen'}_sin_claves.pdf`);
  };

  // -------------------------------------------------------------------------
  // 15.3) ✨ Descarga en TXT con claves
  // -------------------------------------------------------------------------
  const handleDownloadTXTWithKeys = () => {
    let content = `${title || 'Examen'}\n\n`;
    filteredQuestions.forEach((q, index) => {
      content += `Pregunta ${index + 1} (${q.year}):\n`;
      
      if (q.case_description) {
        content += q.case_description + '\n\n';
      }
      content += q.question_text + '\n\n';
      
      // Opciones sin marcar la correcta
      q.options.forEach((opt) => {
        content += `${opt.option_label}) ${opt.option_text}\n`;
      });
      
      // Respuesta correcta al final
      const correctOption = q.options.find(opt => opt.correct);
      if (correctOption) {
        content += `\nRespuesta correcta: ${correctOption.option_label}) ${correctOption.option_text}\n`;
      }
      
      // Separador ASCII para evitar caracteres raros en TXT
      content += '\n' + '-'.repeat(80) + '\n\n';
    });
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title || 'examen'}_con_claves.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // -------------------------------------------------------------------------
  // 15.4) ✨ Descarga en TXT sin claves
  // -------------------------------------------------------------------------
  const handleDownloadTXTWithoutKeys = () => {
    let content = `${title || 'Examen'}\n\n`;
    filteredQuestions.forEach((q, index) => {
      content += `Pregunta ${index + 1} (${q.year}):\n`;

      if (q.case_description) {
        content += q.case_description + '\n\n';
      }
      content += q.question_text + '\n\n';
      
      // Opciones sin marcar la correcta
      q.options.forEach((opt) => {
        content += `${opt.option_label}) ${opt.option_text}\n`;
      });
      
      content += '\n' + '-'.repeat(80) + '\n\n';
    });
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title || 'examen'}_sin_claves.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // -------------------------------------------------------------------------
  // 15.5) ✨ Componente de Dropdown de Descargas
  // -------------------------------------------------------------------------
  const DownloadDropdown = ({ darkMode, isAdmin }) => {
    const dropdownRef = useRef(null);

    // Cerrar dropdown al hacer clic fuera
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setShowDownloadOptions(false);
        }
      };

      if (showDownloadOptions) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [showDownloadOptions]);

    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setShowDownloadOptions(!showDownloadOptions)}
          className={`flex items-center gap-2 px-4 py-2 ${
            darkMode 
              ? 'bg-slate-700/40 hover:bg-slate-600/50 backdrop-blur border border-slate-600/30 hover:shadow-lg hover:shadow-blue-500/10' 
              : 'bg-gray-500 hover:bg-gray-600'
          } text-white rounded-lg transition-all duration-300`}
        >
          <Download className="w-4 h-4" />
          Descargar
          <ChevronDown className={`w-4 h-4 transition-transform ${showDownloadOptions ? 'rotate-180' : ''}`} />
        </button>
        
        {showDownloadOptions && (
          <div className={`absolute top-full mt-2 left-0 w-64 ${
            darkMode 
              ? 'bg-slate-800/95 backdrop-blur-xl border-slate-700/50 shadow-2xl shadow-black/30' 
              : 'bg-white border-gray-200'
          } rounded-xl shadow-xl border z-10 transition-all duration-300`}>
            <div className="p-2">
              <h3 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'} px-2 py-1`}>
                PDF {isAdmin && <span className="text-purple-500 text-xs">(Admin)</span>}
              </h3>
              
              {/* Opciones exclusivas de admin */}
              {isAdmin && (
                <>
                  <button
                    onClick={() => {
                      handleDownloadPDFWithKeys();
                      setShowDownloadOptions(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm ${darkMode ? 'hover:bg-purple-900/30 text-purple-300' : 'hover:bg-purple-100 text-purple-700'} rounded transition-colors`}
                  >
                    👑 PDF con claves (respuestas)
                  </button>
                  <button
                    onClick={() => {
                      handleDownloadPDFWithoutKeys();
                      setShowDownloadOptions(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm ${darkMode ? 'hover:bg-purple-900/30 text-purple-300' : 'hover:bg-purple-100 text-purple-700'} rounded transition-colors`}
                  >
                    👑 PDF sin claves
                  </button>
                </>
              )}
              
              {/* Mensaje para usuarios no admin */}
              {!isAdmin && (
                <div className={`px-3 py-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <p className="text-xs">
                    ⚠️ La descarga de PDF está disponible solo para administradores
                  </p>
                </div>
              )}
              
              {/* Opciones TXT solo para admin */}
              {isAdmin && (
                <>
                  <div className={`border-t ${darkMode ? 'border-purple-700/50' : 'border-purple-200'} my-2`}></div>
                  
                  <h3 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'} px-2 py-1`}>
                    TXT <span className="text-purple-500 text-xs">(Admin)</span>
                  </h3>
                  <button
                    onClick={() => {
                      handleDownloadTXTWithKeys();
                      setShowDownloadOptions(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm ${darkMode ? 'hover:bg-purple-900/30 text-purple-300' : 'hover:bg-purple-100 text-purple-700'} rounded transition-colors`}
                  >
                    👑 TXT con claves (respuestas)
                  </button>
                  <button
                    onClick={() => {
                      handleDownloadTXTWithoutKeys();
                      setShowDownloadOptions(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm ${darkMode ? 'hover:bg-purple-900/30 text-purple-300' : 'hover:bg-purple-100 text-purple-700'} rounded transition-colors`}
                  >
                    👑 TXT sin claves
                  </button>
                  
                  <div className={`border-t ${darkMode ? 'border-purple-700/50' : 'border-purple-200'} my-2`}></div>
                  
                  <h3 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'} px-2 py-1`}>
                    Avanzado <span className="text-purple-500 text-xs">(Admin)</span>
                  </h3>
                  <button
                    onClick={() => {
                      // Exportar como JSON
                      const dataStr = JSON.stringify(filteredQuestions, null, 2);
                      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
                      const exportFileDefaultName = `preguntas_${title}_${new Date().toISOString().split('T')[0]}.json`;
                      const linkElement = document.createElement('a');
                      linkElement.setAttribute('href', dataUri);
                      linkElement.setAttribute('download', exportFileDefaultName);
                      linkElement.click();
                      setShowDownloadOptions(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm ${darkMode ? 'hover:bg-purple-900/30 text-purple-300' : 'hover:bg-purple-100 text-purple-700'} rounded transition-colors`}
                  >
                    👑 Exportar JSON (Base de datos)
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // SISTEMA DE TIMER CON PAUSA PARA MODO SIMULACRO (MEJORADO)
  // -------------------------------------------------------------------------

  // Timer countdown para modo simulacro - IMPLEMENTACIÓN CORRECTA
  useEffect(() => {
    // Solo crear intervalo si:
    // 1. Estamos en modo simulacro
    // 2. Hay tiempo restante
    // 3. No hemos mostrado resultados
    // 4. NO está pausado
    if (simulationMode && timeLeft > 0 && !showResults && !isPaused) {
      console.log('✅ Iniciando/Reanudando timer...', { timeLeft, isPaused });

      const intervalId = setInterval(() => {
        setTimeLeft(prevTime => {
          console.log('⏱ Tick:', prevTime - 1);

          if (prevTime <= 1) {
            // Tiempo agotado
            console.log('⚠️ Tiempo agotado!');
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);

      // Guardar referencia del intervalo
      timerIntervalRef.current = intervalId;

      // MUY IMPORTANTE: Limpiar intervalo cuando el efecto se vuelva a ejecutar
      return () => {
        console.log('🗑 Limpiando intervalo...');
        clearInterval(intervalId);
        timerIntervalRef.current = null;
      };
    } else {
      // Si está pausado o no cumple condiciones, asegurarse de limpiar cualquier intervalo existente
      if (timerIntervalRef.current) {
        console.log('🛑 Deteniendo timer (pausado o condiciones no cumplidas)');
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }

    // Cleanup al desmontar
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [simulationMode, showResults, isPaused]); // IMPORTANTE: NO incluir timeLeft en las dependencias

  // Efecto separado para manejar cuando el tiempo llega a 0
  useEffect(() => {
    if (simulationMode && timeLeft === 0 && !showResults) {
      console.log('⏰ Tiempo en 0, finalizando...');
      finalizarSimulacro(incorrectQuestions);
    }
  }, [timeLeft, simulationMode, showResults, incorrectQuestions, finalizarSimulacro]);

  // -------------------------------------------------------------------------
  // 17) useEffect para guardar examen en el historial localStorage
  // -------------------------------------------------------------------------
  useEffect(() => {
    // skipHistorySave permite omitir el guardado (usado por Práctica Continua que usa IndexedDB)
    if (showResults && simulationMode && !savedHistory && examAnalytics && !skipHistorySave) {
      const now = new Date();
      const payload = buildExamHistoryPayload({
        examTitle: title || 'Examen sin Título',
        simulacroId,
        date: now.toLocaleDateString(),
        time: now.toLocaleTimeString(),
        analytics: examAnalytics,
        meta: {
          timeSpentSeconds,
          allocatedSeconds: totalAllocatedSeconds,
        },
      });
      saveExamToHistory(payload, savedSessionId);
      setSavedHistory(true);
    }
  }, [
    showResults,
    simulationMode,
    savedHistory,
    examAnalytics,
    title,
    timeSpentSeconds,
    totalAllocatedSeconds,
    skipHistorySave,
    savedSessionId,
  ]);

  // -------------------------------------------------------------------------
  // 18) Pantalla final de resultados (modo simulacro)
  // -------------------------------------------------------------------------
  if (showResults && simulationMode) {
    return (
      <SimulationResultsPanel
        darkMode={darkMode}
        finishPromptOverlay={finishPromptOverlay}
        analytics={examAnalytics}
        timeSpentSeconds={timeSpentSeconds}
        allocatedSeconds={totalAllocatedSeconds}
        onReset={resetStudy}
        onBack={onBack}
        fromGenerator={fromGenerator}
        downloadComponent={subscription.isAdmin ? (
          <DownloadDropdown darkMode={darkMode} isAdmin={subscription.isAdmin} />
        ) : null}
      />
    );
  }

  // -------------------------------------------------------------------------
  // 19) Si no hay preguntas tras filtrar
  // -------------------------------------------------------------------------
  if (!filteredQuestions.length || !currentQuestionData) {
    return (
      <>
        {finishPromptOverlay}
        <div className="max-w-4xl mx-auto p-6">
          <div className={`rounded-lg shadow-lg p-6 ${
            darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white'
          }`}>
            <p className={darkMode ? 'text-pink-400' : 'text-red-600'}>
              No hay preguntas disponibles con los filtros aplicados.
            </p>
            <button
              onClick={() => {
                setSelectedYear('Todos');
                setSelectedSubtopic('Todos');
              }}
              className={`mt-4 px-4 py-2 rounded-lg transition-all ${
                darkMode
                  ? 'bg-blue-600 hover:bg-blue-500 text-white'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              Ver Todas
            </button>
          </div>
        </div>
      </>
    );
  }

  // -------------------------------------------------------------------------
  // 20) Render principal (modo estudio o práctica normal)
  // -------------------------------------------------------------------------
  const navButtonBase = 'inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-offset-2';
  const homeButtonStyles = darkMode
    ? 'bg-slate-900/70 border border-slate-700 text-slate-100 hover:bg-slate-800 focus:ring-blue-400 focus:ring-offset-slate-900'
    : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 focus:ring-blue-500 focus:ring-offset-white';
  const backButtonStyles = darkMode
    ? 'bg-gradient-to-r from-amber-500 via-rose-500 to-pink-500 text-white shadow-lg shadow-rose-500/15 hover:from-amber-400 hover:via-rose-400 hover:to-pink-400 focus:ring-rose-300 focus:ring-offset-slate-900'
    : 'bg-gradient-to-r from-amber-500 via-rose-400 to-pink-400 text-white shadow hover:from-amber-400 hover:via-rose-300 hover:to-pink-300 focus:ring-rose-200 focus:ring-offset-white';

  return (
    <div
      className="min-h-screen relative"
      style={{
        background: T.paper,
        color: T.ink,
        fontFamily: '"Manrope", system-ui, sans-serif',
      }}
    >
      {/* Estilos del módulo Quiz — lenguaje "Reading Room" */}
      <style>{`
        @keyframes mep-q-flash {
          0% { box-shadow: 0 0 0 0 ${T.glow}; }
          50% { box-shadow: 0 0 0 8px ${T.glow}; }
          100% { box-shadow: 0 0 0 0 transparent; }
        }
        @keyframes mep-q-glow-in {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes mep-q-shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-2px); }
          75% { transform: translateX(2px); }
        }
        .mep-q-serif { font-family: "Newsreader", "Instrument Serif", serif; }
        .mep-q-sans  { font-family: "Manrope", system-ui, sans-serif; }
        .mep-q-mono  { font-family: "Geist Mono", "JetBrains Mono", monospace; font-variant-numeric: tabular-nums; }
        .mep-q-cap {
          font-family: "Manrope", sans-serif;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
        }
        .mep-q-rule { height: 1px; background: ${T.hairline}; }
        .mep-q-rule-soft { height: 1px; background: ${T.hairlineSoft}; }

        .mep-q-container {
          background: ${T.paperSoft};
          border: 1px solid ${T.hairline};
          box-shadow: ${darkMode
            ? `inset 0 1px 0 rgba(242, 237, 224, 0.025), 0 24px 56px -20px rgba(0,0,0,0.55)`
            : `inset 0 1px 0 rgba(255,255,255,0.55), 0 18px 44px -16px rgba(14, 18, 24, 0.10)`};
        }

        .mep-q-option {
          width: 100%;
          background: ${T.paperSoft};
          border: 1px solid ${T.hairline};
          border-left: 2px solid ${T.hairline};
          color: ${T.inkBody};
          padding: 18px 22px;
          text-align: left;
          cursor: pointer;
          transition: all 0.22s cubic-bezier(0.22, 1, 0.36, 1);
          position: relative;
          font-family: "Newsreader", serif;
          font-size: 16px;
          line-height: 1.55;
          font-variation-settings: "opsz" 14;
        }
        .mep-q-option:hover:not(:disabled) {
          background: ${T.paperWarm};
          border-color: ${T.hairline};
          border-left-color: ${T.accent};
          transform: translateX(2px);
          box-shadow: ${darkMode
            ? `0 6px 20px -8px rgba(0,0,0,0.5)`
            : `0 6px 18px -6px rgba(14,18,24,0.10)`};
        }
        .mep-q-option:disabled {
          cursor: not-allowed;
        }
        .mep-q-option[data-state="selected"] {
          background: ${T.accentSoft};
          border-color: ${T.accent};
          border-left: 2px solid ${T.accent};
          color: ${T.inkBody};
          box-shadow: ${darkMode
            ? `0 0 0 1px ${T.accent}40, 0 0 28px ${T.glow}, inset 0 1px 0 rgba(242,237,224,0.04)`
            : `0 0 0 1px ${T.accent}30, 0 8px 22px -8px rgba(30, 58, 111, 0.18)`};
        }
        .mep-q-option[data-state="correct"] {
          background: ${T.sageSoft};
          border-color: ${T.sage};
          border-left: 2px solid ${T.sage};
          color: ${T.inkBody};
          box-shadow: ${darkMode
            ? `0 0 0 1px ${T.sage}50, 0 0 28px ${T.sageGlow}, inset 0 1px 0 rgba(242,237,224,0.04)`
            : `0 0 0 1px ${T.sage}40, 0 8px 22px -8px rgba(80, 107, 92, 0.18)`};
        }
        .mep-q-option[data-state="incorrect"] {
          background: ${T.crimsonSoft};
          border-color: ${T.crimson};
          border-left: 2px solid ${T.crimson};
          color: ${T.inkBody};
          opacity: 0.85;
          animation: mep-q-shake 0.4s ease-in-out;
        }
        .mep-q-option-label {
          display: inline-block;
          width: 26px;
          height: 26px;
          line-height: 26px;
          text-align: center;
          font-family: "Geist Mono", monospace;
          font-size: 11px;
          font-weight: 600;
          color: ${T.accent};
          background: transparent;
          border: 1px solid ${T.hairline};
          margin-right: 14px;
          flex-shrink: 0;
          letter-spacing: 0.04em;
          transition: all 0.22s;
        }
        .mep-q-option:hover:not(:disabled) .mep-q-option-label {
          border-color: ${T.accent};
          color: ${T.accent};
          background: ${T.accentTint};
        }
        .mep-q-option[data-state="selected"] .mep-q-option-label {
          background: ${T.accent};
          color: ${darkMode ? '#07090D' : T.paper};
          border-color: ${T.accent};
        }
        .mep-q-option[data-state="correct"] .mep-q-option-label {
          background: ${T.sage};
          color: ${darkMode ? '#07090D' : T.paper};
          border-color: ${T.sage};
        }
        .mep-q-option[data-state="incorrect"] .mep-q-option-label {
          background: ${T.crimson};
          color: ${darkMode ? '#07090D' : T.paper};
          border-color: ${T.crimson};
        }

        .mep-q-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 7px 14px;
          font-family: "Manrope", sans-serif;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.02em;
          color: ${T.inkSoft};
          background: transparent;
          border: 1px solid ${T.hairline};
          cursor: pointer;
          transition: all 0.18s;
        }
        .mep-q-btn:hover:not(:disabled) {
          color: ${T.ink};
          border-color: ${T.hairline};
          background: ${T.paperWarm};
        }
        .mep-q-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .mep-q-btn[data-active="true"] {
          color: ${darkMode ? '#07090D' : T.paper};
          background: ${T.accent};
          border-color: ${T.accent};
        }
        .mep-q-btn[data-tone="accent"] {
          color: ${T.accent};
          border-color: ${T.accent};
        }
        .mep-q-btn[data-tone="accent"]:hover:not(:disabled) {
          background: ${T.accent};
          color: ${darkMode ? '#07090D' : T.paper};
          ${darkMode ? `box-shadow: 0 0 0 1px ${T.accent}, 0 0 16px ${T.glow};` : ''}
        }
        .mep-q-btn[data-tone="sage"] {
          color: ${T.sage};
          border-color: ${T.sage};
        }
        .mep-q-btn[data-tone="sage"]:hover:not(:disabled) {
          background: ${T.sage};
          color: ${darkMode ? '#07090D' : T.paper};
        }
        .mep-q-btn[data-tone="crimson"] {
          color: ${T.crimson};
          border-color: ${T.crimson};
        }
        .mep-q-btn[data-tone="crimson"]:hover:not(:disabled) {
          background: ${T.crimson};
          color: ${darkMode ? '#07090D' : T.paper};
        }

        .mep-q-input {
          background: ${T.paperSoft};
          border: 1px solid ${T.hairline};
          color: ${T.ink};
          font-family: "Manrope", sans-serif;
          padding: 8px 12px;
          transition: border-color 0.18s, box-shadow 0.2s;
        }
        .mep-q-input:focus {
          outline: none;
          border-color: ${T.accent};
          ${darkMode ? `box-shadow: 0 0 0 3px ${T.glow};` : ''}
        }

        .mep-q-progress {
          height: 3px;
          background: ${T.hairlineSoft};
          width: 100%;
          position: relative;
          overflow: hidden;
        }
        .mep-q-progress-fill {
          position: absolute;
          inset: 0 auto 0 0;
          background: ${T.accent};
          transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
          ${darkMode ? `box-shadow: 0 0 8px ${T.glow};` : ''}
        }
        .mep-q-progress-correct {
          position: absolute;
          inset: 0 auto 0 0;
          background: ${T.sage};
          transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
          opacity: 0.7;
        }

        .mep-q-corner-mark {
          position: absolute;
          background: ${T.accent};
        }

        .mep-q-explanation {
          background: ${T.paperSoft};
          border: 1px solid ${T.hairline};
          border-left: 3px solid ${T.accent};
          padding: 24px 28px;
          ${darkMode ? `box-shadow: inset 0 1px 0 rgba(242,237,224,0.025);` : ''}
        }
      `}</style>

      {finishPromptOverlay}
      {/* Pause overlay editorial */}
      <AnimatePresence>
        {simulationMode && isPaused && showPauseOverlay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)' }}
            onClick={() => setShowPauseOverlay(false)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0, y: 8 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0, y: 8 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-md w-full relative"
              style={{
                background: T.paperSoft,
                border: `1px solid ${T.hairline}`,
                padding: '40px 36px 32px',
                ...(darkMode && { boxShadow: `0 32px 80px -20px rgba(0,0,0,0.7), inset 0 1px 0 rgba(242,237,224,0.04)` }),
              }}
            >
              {/* 4 corner marks */}
              <div className="absolute top-0 left-0 w-7 h-px" style={{ background: T.accent }} />
              <div className="absolute top-0 left-0 w-px h-7" style={{ background: T.accent }} />
              <div className="absolute top-0 right-0 w-7 h-px" style={{ background: T.accent }} />
              <div className="absolute top-0 right-0 w-px h-7" style={{ background: T.accent }} />
              <div className="absolute bottom-0 left-0 w-7 h-px" style={{ background: T.accent }} />
              <div className="absolute bottom-0 left-0 w-px h-7" style={{ background: T.accent }} />
              <div className="absolute bottom-0 right-0 w-7 h-px" style={{ background: T.accent }} />
              <div className="absolute bottom-0 right-0 w-px h-7" style={{ background: T.accent }} />

              <p className="mep-q-cap mb-2" style={{ color: T.accent, letterSpacing: '0.32em' }}>
                ⏸ pausado
              </p>
              <h2
                style={{
                  fontFamily: '"Newsreader", serif',
                  fontStyle: 'italic',
                  fontSize: '2rem',
                  color: T.ink,
                  letterSpacing: '-0.01em',
                  fontWeight: 400,
                  marginBottom: '24px',
                }}
              >
                Examen detenido
              </h2>

              <div
                className="space-y-3 pb-5 mb-5"
                style={{ borderBottom: `1px solid ${T.hairlineSoft}` }}
              >
                <div className="flex items-baseline justify-between">
                  <span className="mep-q-cap" style={{ color: T.inkMute }}>tiempo restante</span>
                  <span
                    style={{
                      fontFamily: '"Geist Mono", monospace',
                      fontVariantNumeric: 'tabular-nums',
                      fontSize: '20px',
                      color: T.accent,
                      fontWeight: 500,
                    }}
                  >
                    {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="mep-q-cap" style={{ color: T.inkMute }}>respondidas</span>
                  <span
                    style={{
                      fontFamily: '"Geist Mono", monospace',
                      fontVariantNumeric: 'tabular-nums',
                      fontSize: '20px',
                      color: T.sage,
                      fontWeight: 500,
                    }}
                  >
                    {answeredCount} / {filteredQuestions.length}
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="mep-q-cap" style={{ color: T.inkMute }}>pausa acumulada</span>
                  <span
                    style={{
                      fontFamily: '"Geist Mono", monospace',
                      fontVariantNumeric: 'tabular-nums',
                      fontSize: '15px',
                      color: T.inkSoft,
                    }}
                  >
                    {Math.floor(totalPausedTime / 60000)}:{Math.floor((totalPausedTime % 60000) / 1000).toString().padStart(2, '0')}
                  </span>
                </div>
              </div>

              <p
                style={{
                  color: T.inkSoft,
                  fontFamily: '"Newsreader", serif',
                  fontStyle: 'italic',
                  fontSize: '13.5px',
                  lineHeight: 1.55,
                  marginBottom: '24px',
                }}
              >
                Tu progreso queda guardado. Tómate el tiempo que necesites — al reanudar volverás exactamente a donde estabas.
              </p>

              <div className="flex gap-2">
                <button
                  onClick={handleResume}
                  className="flex-1 flex items-center justify-center gap-2"
                  style={{
                    padding: '12px 20px',
                    background: T.accent,
                    color: darkMode ? '#07090D' : T.paper,
                    fontFamily: '"Manrope", sans-serif',
                    fontWeight: 600,
                    fontSize: '12.5px',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    border: `1px solid ${T.accent}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    ...(darkMode && { boxShadow: `0 0 20px ${T.glow}` }),
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = T.accentDeep; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = T.accent; }}
                >
                  <Play size={13} />
                  reanudar
                </button>
                <button
                  onClick={() => setShowPauseOverlay(false)}
                  className="mep-q-btn"
                  style={{ padding: '12px 18px' }}
                >
                  navegar
                </button>
              </div>

              <p
                className="mep-q-cap mt-5 text-center"
                style={{ color: T.inkMute, letterSpacing: '0.20em', fontSize: '9px' }}
              >
                ␣ espacio · esc · p · m · n
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Paleta de preguntas (simulacro) — editorial grid */}
      <AnimatePresence>
        {simulationMode && !showResults && showPalette && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)' }}
            onClick={() => setShowPalette(false)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0, y: 8 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0, y: 8 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-5xl max-h-[80vh] overflow-hidden relative"
              style={{
                background: T.paperSoft,
                border: `1px solid ${T.hairline}`,
                ...(darkMode && { boxShadow: `0 32px 80px -20px rgba(0,0,0,0.7)` }),
              }}
            >
              {/* Header de la paleta */}
              <div
                className="px-6 py-4 flex items-center justify-between flex-wrap gap-3"
                style={{ borderBottom: `1px solid ${T.hairline}` }}
              >
                <div className="flex items-baseline gap-4">
                  <span className="mep-q-cap" style={{ color: T.accent, letterSpacing: '0.32em' }}>
                    ⟢ paleta
                  </span>
                  <div className="flex items-baseline gap-3">
                    <span style={{ fontFamily: '"Geist Mono", monospace', fontSize: '12px', color: T.sage }}>
                      {userAnswers.filter(v => v != null).length} respondidas
                    </span>
                    <span style={{ color: T.hairline }}>·</span>
                    <span style={{ fontFamily: '"Geist Mono", monospace', fontSize: '12px', color: T.crimson }}>
                      {Math.max(0, filteredQuestions.length - userAnswers.filter(v => v != null).length)} pendientes
                    </span>
                    <span style={{ color: T.hairline }}>·</span>
                    <span style={{ fontFamily: '"Geist Mono", monospace', fontSize: '12px', color: T.accent }}>
                      {Object.values(markedForReview).filter(Boolean).length} marcadas
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer" style={{ color: T.inkSoft }}>
                    <input
                      type="checkbox"
                      checked={paletteOnlyUnanswered}
                      onChange={(e) => setPaletteOnlyUnanswered(e.target.checked)}
                      style={{ accentColor: T.accent }}
                    />
                    <span className="mep-q-cap" style={{ color: T.inkSoft }}>solo pendientes</span>
                  </label>
                  <button onClick={() => setShowPalette(false)} className="mep-q-btn">
                    cerrar
                  </button>
                </div>
              </div>

              {/* Grid de preguntas */}
              <div className="p-6 overflow-y-auto max-h-[70vh]">
                <div className="grid grid-cols-10 sm:grid-cols-12 lg:grid-cols-14 gap-1.5">
                  {filteredQuestions.map((q, idx) => {
                    const answered = userAnswers[idx] != null;
                    if (paletteOnlyUnanswered && answered) return null;
                    const marked = !!markedForReview[idx];
                    const isCurrent = idx === currentQuestion;
                    const disabled = simulationMode && isPaused;
                    const hasNote = !!questionNotes[idx]?.text;

                    let bg = 'transparent', color = T.ink, border = T.hairline;
                    if (isCurrent) { bg = T.accent; color = darkMode ? '#07090D' : T.paper; border = T.accent; }
                    else if (marked) { bg = T.accentSoft; color = T.accent; border = T.accent; }
                    else if (answered) { bg = T.sageSoft; color = T.sage; border = T.sage; }

                    return (
                      <button
                        key={idx}
                        onClick={() => {
                          if (disabled) return;
                          setCurrentQuestion(idx);
                          setShowPalette(false);
                        }}
                        disabled={disabled}
                        style={{
                          position: 'relative',
                          height: '36px',
                          background: bg,
                          color,
                          border: `1px solid ${border}`,
                          fontFamily: '"Geist Mono", monospace',
                          fontVariantNumeric: 'tabular-nums',
                          fontSize: '11.5px',
                          fontWeight: 500,
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          opacity: disabled ? 0.4 : 1,
                          transition: 'all 0.18s',
                          ...(isCurrent && darkMode && { boxShadow: `0 0 14px ${T.glow}` }),
                        }}
                        onMouseEnter={(e) => {
                          if (disabled || isCurrent) return;
                          e.currentTarget.style.borderColor = T.accent;
                        }}
                        onMouseLeave={(e) => {
                          if (disabled || isCurrent) return;
                          e.currentTarget.style.borderColor = border;
                        }}
                        title={(() => {
                          if (marked) return 'Marcada para revisar' + (hasNote ? ' (con nota)' : '');
                          if (answered) {
                            const ansOpt = (q.options || []).find((o) => o.id === userAnswers[idx]);
                            const label = ansOpt?.option_label || '';
                            return (label ? `Clave: ${label}` : 'Respondida') + (hasNote ? ' (con nota)' : '');
                          }
                          return 'Sin responder' + (hasNote ? ' (con nota)' : '');
                        })()}
                      >
                        {idx + 1}
                        {hasNote && (
                          <span
                            style={{
                              position: 'absolute',
                              top: '-2px',
                              right: '-2px',
                              width: '5px',
                              height: '5px',
                              borderRadius: '50%',
                              background: T.accent,
                              boxShadow: darkMode ? `0 0 4px ${T.accent}` : 'none',
                            }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Leyenda */}
                <div
                  className="mt-6 pt-5 flex items-center gap-5 flex-wrap"
                  style={{ borderTop: `1px solid ${T.hairlineSoft}` }}
                >
                  <span className="mep-q-cap" style={{ color: T.inkMute }}>leyenda</span>
                  <span className="flex items-center gap-2 text-xs" style={{ color: T.inkSoft }}>
                    <span style={{ width: 14, height: 14, background: T.accent, border: `1px solid ${T.accent}` }} />
                    actual
                  </span>
                  <span className="flex items-center gap-2 text-xs" style={{ color: T.inkSoft }}>
                    <span style={{ width: 14, height: 14, background: T.sageSoft, border: `1px solid ${T.sage}` }} />
                    respondida
                  </span>
                  <span className="flex items-center gap-2 text-xs" style={{ color: T.inkSoft }}>
                    <span style={{ width: 14, height: 14, background: T.accentSoft, border: `1px solid ${T.accent}` }} />
                    marcada
                  </span>
                  <span className="flex items-center gap-2 text-xs" style={{ color: T.inkSoft }}>
                    <span style={{ width: 14, height: 14, background: 'transparent', border: `1px solid ${T.hairline}` }} />
                    pendiente
                  </span>
                  <span className="flex items-center gap-2 text-xs" style={{ color: T.inkSoft }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: T.accent }} />
                    con nota
                  </span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div
        className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-10 py-8 mep-q-container relative"
        style={{
          background: T.paperSoft,
          border: `1px solid ${T.hairline}`,
          ...(simulationMode && isCurrentAnswered && {
            borderLeft: `2px solid ${T.accent}`,
            ...(darkMode && { boxShadow: `0 0 0 1px ${T.accent}30, 0 0 32px ${T.glow}, inset 0 1px 0 rgba(242,237,224,0.025)` }),
          }),
        }}
      >
        {/* Nav superior — minimal hairline */}
        <div
          className="flex items-center gap-5 pb-4 mb-7"
          style={{ borderBottom: `1px solid ${T.hairlineSoft}` }}
        >
          {onGoHome && (
            <button
              onClick={onGoHome}
              className="flex items-center gap-2 group"
              style={{ color: T.inkSoft, fontFamily: '"Manrope", sans-serif' }}
            >
              <Home size={13} />
              <span className="text-sm tracking-wide group-hover:underline" style={{ color: T.ink }}>
                Inicio
              </span>
            </button>
          )}
          {onBack && (
            <>
              <span style={{ color: T.hairline }}>·</span>
              <button
                onClick={onBack}
                className="flex items-center gap-2 group"
                style={{ color: T.inkSoft, fontFamily: '"Manrope", sans-serif' }}
              >
                <ChevronLeft size={13} />
                <span className="text-sm tracking-wide group-hover:underline" style={{ color: T.ink }}>
                  {fromGenerator ? 'Volver al generador' : 'Regresar'}
                </span>
              </button>
            </>
          )}
          <div className="ml-auto flex items-center gap-2">
            <span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: simulationMode ? T.accent : T.sage }}
            />
            <span className="mep-q-cap" style={{ color: T.inkMute }}>
              {simulationMode ? 'simulacro' : 'estudio'}
            </span>
          </div>
        </div>

        {/* Título + Streak + Timer + Controls */}
        <div className="mb-7">
          <div className="flex justify-between items-start gap-6 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="mep-q-cap mb-2" style={{ color: T.accent }}>
                ⟢ examen en curso
              </p>
              <h1
                className="leading-[1.05] tracking-[-0.015em]"
                style={{
                  fontFamily: '"Newsreader", serif',
                  fontWeight: 400,
                  fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                  color: T.ink,
                }}
              >
                {title}
              </h1>

              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <AnimatePresence>
                  <StreakIndicator streak={streak} darkMode={darkMode} />
                </AnimatePresence>
              </div>
            </div>

            {/* Timer + controls — solo en simulacro */}
            {simulationMode && timeLeft !== null && (
              <div className="flex flex-col items-end gap-4">
                <div className="flex items-start gap-4">
                  <CircularTimer
                    timeLeft={timeLeft}
                    totalTime={simulationTime * 60 + extraTime}
                    darkMode={darkMode}
                    isPaused={isPaused}
                    onPause={handlePause}
                    onResume={handleResume}
                  />
                  <QuestionTimer
                    currentQuestion={currentQuestion}
                    isPaused={isPaused}
                    darkMode={darkMode}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Controls bar — solo en simulacro, paleta unificada */}
          {simulationMode && timeLeft !== null && (
            <div
              className="mt-5 pt-5 flex items-center gap-2 flex-wrap"
              style={{ borderTop: `1px solid ${T.hairlineSoft}` }}
            >
              <button
                onClick={() => {
                  if (!simulationMode || showResults) return;
                  setExtraTime(prev => prev + 300);
                  setTimeLeft(prev => (prev ?? 0) + 300);
                }}
                className="mep-q-btn"
                title="Agregar 5 min"
              >
                <Clock size={11} /> +5 min
              </button>
              <button
                onClick={() => setMarkedForReview(prev => ({ ...(prev || {}), [currentQuestion]: !prev[currentQuestion] }))}
                disabled={isPaused}
                className="mep-q-btn"
                data-active={!!markedForReview[currentQuestion]}
                title="Marcar para revisar (M)"
              >
                {markedForReview[currentQuestion] ? '✓ marcada' : 'marcar'}
              </button>
              <button
                onClick={() => setShowNotePanel(prev => !prev)}
                disabled={isPaused}
                className="mep-q-btn"
                data-active={showNotePanel}
                title="Nota de coaching (N)"
              >
                nota
              </button>
              <button
                onClick={() => setShowPalette(true)}
                disabled={isPaused}
                className="mep-q-btn"
                title="Paleta (P)"
              >
                paleta
              </button>
              <button
                onClick={saveProgress}
                disabled={isPaused}
                className="mep-q-btn"
                data-tone="sage"
                title="Guardar progreso en MySQL"
              >
                {saveProgressMsg || (savedSessionId ? 'actualizar' : 'guardar')}
              </button>
              <div style={{ flex: 1 }} />
              <button onClick={handleFinalizeConfirm} className="mep-q-btn" data-tone="crimson" title="Finalizar simulacro">
                finalizar →
              </button>
              <button onClick={descartarSimulacro} className="mep-q-btn" title="Descartar sin guardar">
                descartar
              </button>
            </div>
          )}

          {/* Keypad A-E + Borrar — solo en simulacro, debajo de controls */}
          {simulationMode && timeLeft !== null && (
            <div className="mt-3 hidden md:flex items-center gap-1.5 flex-wrap">
              <span className="mep-q-cap mr-2" style={{ color: T.inkMute }}>
                respuesta:
              </span>
              {keypadLayout.map((key) => {
                const isActive = key.optionId != null && key.optionId === currentAnswerId;
                const isDisabled = !key.optionId || key.disabled || (simulationMode && isPaused);
                return (
                  <button
                    key={key.label}
                    type="button"
                    onClick={() => key.optionId && setAnswerByOptionId(key.optionId)}
                    disabled={isDisabled}
                    style={{
                      width: '32px',
                      height: '32px',
                      fontFamily: '"Geist Mono", monospace',
                      fontSize: '12px',
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      color: isActive ? (darkMode ? '#07090D' : T.paper) : T.ink,
                      background: isActive ? T.accent : 'transparent',
                      border: `1px solid ${isActive ? T.accent : T.hairline}`,
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      opacity: isDisabled ? 0.35 : 1,
                      transition: 'all 0.18s',
                      ...(isActive && darkMode && { boxShadow: `0 0 12px ${T.glow}` }),
                    }}
                    onMouseEnter={(e) => {
                      if (isActive || isDisabled) return;
                      e.currentTarget.style.borderColor = T.accent;
                      e.currentTarget.style.color = T.accent;
                    }}
                    onMouseLeave={(e) => {
                      if (isActive || isDisabled) return;
                      e.currentTarget.style.borderColor = T.hairline;
                      e.currentTarget.style.color = T.ink;
                    }}
                  >
                    {key.label}
                  </button>
                );
              })}
              <button
                onClick={clearCurrentAnswer}
                disabled={!isCurrentAnswered || (simulationMode && isPaused)}
                className="mep-q-btn"
                style={{ marginLeft: '6px', padding: '5px 10px' }}
                title="Borrar respuesta (Backspace)"
              >
                ← borrar
              </button>
              <span className="mep-q-cap ml-auto" style={{ color: T.inkMute, fontSize: '8.5px' }}>
                A-E / 1-5 · Espacio: pausa · M: marcar · N: nota · P: paleta · F: finalizar
              </span>
            </div>
          )}

          {/* Indicador de plan — barra editorial discreta */}
          {!simulationMode && (
            <div
              className="mb-4 px-4 py-2 flex items-center justify-between flex-wrap gap-2"
              style={{
                background: subscription.isAdmin ? T.accentTint : T.paperWarm,
                border: `1px solid ${T.hairlineSoft}`,
                borderLeft: `2px solid ${subscription.isAdmin ? T.accent : T.hairline}`,
              }}
            >
              <span
                style={{
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: '11px',
                  fontWeight: 500,
                  color: T.ink,
                  letterSpacing: '0.02em',
                }}
              >
                {subscription.isAdmin ? (
                  <>
                    <span className="mep-q-cap" style={{ color: T.accent, marginRight: 8 }}>admin</span>
                    Acceso total {subscription.isDevelopment && '· dev'}
                  </>
                ) : !user ? (
                  <>
                    <span className="mep-q-cap" style={{ color: T.crimson, marginRight: 8 }}>invitado</span>
                    {Math.max(0, 5 - questionsAnswered)}/5 preguntas restantes
                  </>
                ) : subscription.plan === 'free' ? (
                  <>
                    <span className="mep-q-cap" style={{ color: T.inkMute, marginRight: 8 }}>gratuito</span>
                    {Math.max(0, 15 - questionsAnswered)}/15 preguntas restantes
                  </>
                ) : (
                  <>
                    <span className="mep-q-cap" style={{ color: T.sage, marginRight: 8 }}>pro</span>
                    Preguntas ilimitadas
                  </>
                )}
              </span>
              {!subscription.isAdmin && ((!user && questionsAnswered >= 3) || (subscription.plan === 'free' && questionsAnswered >= 10)) && (
                <button
                  onClick={() => {
                    if (!user) {
                      setUpgradeMessage(showUpgradePrompt('loginRequired'));
                      setShowLoginModal(true);
                    } else {
                      setUpgradeMessage(showUpgradePrompt('upgradeRequired'));
                      setShowUpgradeModal(true);
                    }
                  }}
                  className="mep-q-btn"
                  data-tone="accent"
                  style={{ padding: '4px 12px', fontSize: '11px' }}
                >
                  {!user ? 'registrarse →' : 'actualizar a pro →'}
                </button>
              )}
            </div>
          )}
          
          {/* Barra de Progreso Visual */}
          <ProgressBar 
            current={currentQuestion + 1} 
            total={filteredQuestions.length} 
            correctAnswers={correctAnswersCount}
            darkMode={darkMode}
            darkModeStyle={darkModeStyle}
            simulationMode={simulationMode}
            isAnswered={isCurrentAnswered}
          />
          
          {/* Estadísticas en Tiempo Real */}
          {!simulationMode && (
            <RealTimeStats
              totalQuestions={filteredQuestions.length}
              correctAnswers={correctAnswersCount}
              incorrectAnswers={incorrectAnswersCount}
              timeSpent={totalTimeSpent}
              darkMode={darkMode}
              darkModeStyle={darkModeStyle}
            />
          )}

          <div className="flex justify-between items-center flex-wrap gap-3 mt-4">
            {!simulationMode && studyHistory.attempts > 0 && (
              <div className="flex items-baseline gap-2">
                <span className="mep-q-cap" style={{ color: T.inkMute }}>historial:</span>
                <span style={{ fontFamily: '"Geist Mono", monospace', fontSize: '12px', color: T.ink }}>
                  {studyHistory.attempts} intentos
                </span>
                <span style={{ color: T.hairline }}>·</span>
                <span style={{ fontFamily: '"Geist Mono", monospace', fontSize: '12px', color: T.sage }}>
                  {studyHistory.correct} correctas
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 ml-auto">
              {subscription.isAdmin && <DownloadDropdown darkMode={darkMode} isAdmin={subscription.isAdmin} />}
              <button onClick={resetStudy} className="mep-q-btn" data-tone="crimson">
                reiniciar
              </button>
              <button onClick={() => setReviewMode(!reviewMode)} className="mep-q-btn" data-tone="accent">
                {reviewMode ? 'volver a práctica' : 'ver estadísticas'}
              </button>
            </div>
          </div>
        </div>

        {/* Filtros en modo estudio — minimal editorial */}
        {!simulationMode && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="mep-q-cap block mb-2" style={{ color: T.inkMute }}>filtrar por año</label>
              <select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setCurrentQuestion(0);
                }}
                className="mep-q-input w-full"
              >
                <option value="Todos">Todos los años</option>
                {years.sort().map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mep-q-cap block mb-2" style={{ color: T.inkMute }}>filtrar por subtema</label>
              <select
                value={selectedSubtopic}
                onChange={(e) => {
                  setSelectedSubtopic(e.target.value);
                  setCurrentQuestion(0);
                }}
                className="mep-q-input w-full"
              >
                <option value="Todos">Todos los subtemas</option>
                {subtopics.map((st) => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
            </div>
            <div style={{ position: 'relative' }} ref={questionDropdownRef}>
              <label className="mep-q-cap mb-2 flex items-center justify-between" style={{ color: T.inkMute }}>
                <span>buscar pregunta · multi-select</span>
                <span className="mep-q-mono" style={{ fontSize: '9px', color: T.inkMute }}>
                  {availableQuestionsForLookup.length} disp.
                </span>
              </label>

              {/* Trigger del combobox */}
              <button
                type="button"
                onClick={() => setShowQuestionDropdown((s) => !s)}
                className="mep-q-input w-full flex items-center justify-between gap-2"
                style={{
                  textAlign: 'left',
                  cursor: 'pointer',
                  ...(showQuestionDropdown && {
                    borderColor: T.accent,
                    ...(darkMode && { boxShadow: `0 0 0 3px ${T.glow}` }),
                  }),
                }}
              >
                {selectedQuestionIds.size === 0 ? (
                  <span
                    style={{
                      fontFamily: '"Newsreader", serif',
                      fontStyle: 'italic',
                      fontSize: '13px',
                      color: T.inkMute,
                    }}
                  >
                    Selecciona preguntas…
                  </span>
                ) : selectedQuestionIds.size === 1 ? (
                  <span className="flex items-baseline gap-2" style={{ minWidth: 0 }}>
                    <span
                      style={{
                        fontFamily: '"Geist Mono", monospace',
                        fontVariantNumeric: 'tabular-nums',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: T.accent,
                        letterSpacing: '0.04em',
                      }}
                    >
                      #{[...selectedQuestionIds][0]}
                    </span>
                    <span
                      className="truncate"
                      style={{
                        fontFamily: '"Newsreader", serif',
                        fontStyle: 'italic',
                        fontSize: '13px',
                        color: T.inkSoft,
                        minWidth: 0,
                      }}
                    >
                      {filteredQuestions[0]?.subtopic_specific || filteredQuestions[0]?.subtopic || ''}
                    </span>
                  </span>
                ) : (
                  <span className="flex items-baseline gap-2">
                    <span
                      style={{
                        fontFamily: '"Geist Mono", monospace',
                        fontVariantNumeric: 'tabular-nums',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: T.accent,
                      }}
                    >
                      {selectedQuestionIds.size}
                    </span>
                    <span
                      style={{
                        fontFamily: '"Newsreader", serif',
                        fontStyle: 'italic',
                        fontSize: '13px',
                        color: T.inkSoft,
                      }}
                    >
                      preguntas seleccionadas
                    </span>
                  </span>
                )}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {selectedQuestionIds.size > 0 && (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        clearAllQuestionSelections();
                      }}
                      style={{
                        cursor: 'pointer',
                        color: T.crimson,
                        fontSize: '14px',
                        lineHeight: 1,
                      }}
                      title="Limpiar toda la selección"
                    >
                      ✕
                    </span>
                  )}
                  <ChevronDown
                    size={14}
                    style={{
                      color: T.inkSoft,
                      transition: 'transform 0.2s',
                      transform: showQuestionDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  />
                </div>
              </button>

              {/* Dropdown */}
              <AnimatePresence>
                {showQuestionDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.18 }}
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 6px)',
                      left: 0,
                      right: 0,
                      maxHeight: '420px',
                      background: T.paperSoft,
                      border: `1px solid ${T.accent}`,
                      borderLeft: `2px solid ${T.accent}`,
                      zIndex: 50,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      ...(darkMode
                        ? { boxShadow: `0 16px 40px -12px rgba(0,0,0,0.6), 0 0 22px ${T.glow}` }
                        : { boxShadow: `0 12px 32px -10px rgba(30, 58, 111, 0.22)` }),
                    }}
                  >
                    {/* Search input sticky top */}
                    <div
                      style={{
                        padding: '10px 12px',
                        background: T.paperWarm,
                        borderBottom: `1px solid ${T.hairline}`,
                      }}
                    >
                      <input
                        type="text"
                        autoFocus
                        value={dropdownSearch}
                        onChange={(e) => setDropdownSearch(e.target.value)}
                        placeholder="Buscar por # ID, subtema, texto…"
                        className="w-full"
                        style={{
                          background: T.paperSoft,
                          border: `1px solid ${T.hairline}`,
                          color: T.ink,
                          padding: '7px 11px',
                          fontFamily: '"Manrope", sans-serif',
                          fontSize: '12.5px',
                          outline: 'none',
                          transition: 'border-color 0.18s',
                        }}
                        onFocus={(e) => { e.currentTarget.style.borderColor = T.accent; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = T.hairline; }}
                      />

                      {/* Counter + bulk actions */}
                      <div className="flex items-baseline justify-between mt-2 gap-2 flex-wrap">
                        <span className="mep-q-cap" style={{ color: T.inkMute, fontSize: '9px' }}>
                          {dropdownMatchingQuestions.length} de {availableQuestionsForLookup.length}
                          {selectedQuestionIds.size > 0 && (
                            <span style={{ color: T.accent, marginLeft: 8 }}>
                              · {selectedQuestionIds.size} seleccionadas
                            </span>
                          )}
                        </span>
                        <div className="flex items-center gap-3">
                          {dropdownMatchingQuestions.length > 0 && (
                            <button
                              onClick={
                                visibleSelectedCount === dropdownMatchingQuestions.length
                                  ? deselectAllVisibleInDropdown
                                  : selectAllVisibleInDropdown
                              }
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: T.accent,
                                fontSize: '9.5px',
                                fontWeight: 600,
                                letterSpacing: '0.16em',
                                textTransform: 'uppercase',
                                cursor: 'pointer',
                                padding: 0,
                              }}
                            >
                              {visibleSelectedCount === dropdownMatchingQuestions.length
                                ? '✓ deseleccionar visibles'
                                : `seleccionar visibles (${dropdownMatchingQuestions.length})`}
                            </button>
                          )}
                          {selectedQuestionIds.size > 0 && (
                            <button
                              onClick={clearAllQuestionSelections}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: T.crimson,
                                fontSize: '9.5px',
                                fontWeight: 600,
                                letterSpacing: '0.16em',
                                textTransform: 'uppercase',
                                cursor: 'pointer',
                                padding: 0,
                              }}
                            >
                              ✕ limpiar todo
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Lista scrollable con checkboxes */}
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                      {dropdownMatchingQuestions.length === 0 ? (
                        <div
                          style={{
                            padding: '24px 16px',
                            textAlign: 'center',
                            color: T.inkMute,
                            fontFamily: '"Newsreader", serif',
                            fontStyle: 'italic',
                            fontSize: '13px',
                          }}
                        >
                          sin resultados para "{dropdownSearch}"
                        </div>
                      ) : (
                        dropdownMatchingQuestions.map((q, idx) => {
                          const numId = extractNumericId(q.id);
                          const isSelected = selectedQuestionIds.has(numId);
                          const snippet = q.case_description || q.question_text || '';
                          const trimmed = snippet.length > 90 ? snippet.slice(0, 90) + '…' : snippet;

                          return (
                            <button
                              key={`q-dd-${q.id}-${idx}`}
                              onClick={() => toggleQuestionSelection(q.id)}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '10px',
                                width: '100%',
                                textAlign: 'left',
                                padding: '10px 14px',
                                background: isSelected ? T.accentSoft : 'transparent',
                                border: 'none',
                                borderBottom: `1px solid ${T.hairlineSoft}`,
                                borderLeft: `2px solid ${isSelected ? T.accent : 'transparent'}`,
                                cursor: 'pointer',
                                transition: 'background 0.12s, border-left-color 0.12s',
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.background = T.paperWarm;
                                  e.currentTarget.style.borderLeftColor = T.accent;
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.background = 'transparent';
                                  e.currentTarget.style.borderLeftColor = 'transparent';
                                }
                              }}
                            >
                              {/* Checkbox custom */}
                              <span
                                style={{
                                  flexShrink: 0,
                                  width: '14px',
                                  height: '14px',
                                  border: `1px solid ${isSelected ? T.accent : T.hairline}`,
                                  background: isSelected ? T.accent : T.paperSoft,
                                  position: 'relative',
                                  marginTop: '3px',
                                  transition: 'all 0.15s',
                                }}
                              >
                                {isSelected && (
                                  <span
                                    style={{
                                      position: 'absolute',
                                      top: '1px',
                                      left: '4px',
                                      width: '3px',
                                      height: '7px',
                                      border: `solid ${darkMode ? '#07090D' : T.paper}`,
                                      borderWidth: '0 1.5px 1.5px 0',
                                      transform: 'rotate(45deg)',
                                    }}
                                  />
                                )}
                              </span>

                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                                  <span
                                    style={{
                                      fontFamily: '"Geist Mono", monospace',
                                      fontVariantNumeric: 'tabular-nums',
                                      fontSize: '11.5px',
                                      fontWeight: 600,
                                      color: T.accent,
                                      letterSpacing: '0.04em',
                                    }}
                                  >
                                    #{numId}
                                  </span>
                                  {q.year && (
                                    <span
                                      style={{
                                        fontFamily: '"Geist Mono", monospace',
                                        fontSize: '9.5px',
                                        color: T.inkMute,
                                        padding: '1.5px 5px',
                                        border: `1px solid ${T.hairlineSoft}`,
                                        background: T.paperWarm,
                                      }}
                                    >
                                      {q.year}
                                    </span>
                                  )}
                                  {(q.subtopic_specific || q.subtopic) && (
                                    <span
                                      style={{
                                        fontFamily: '"Manrope", sans-serif',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        color: T.ink,
                                      }}
                                    >
                                      {q.subtopic_specific || q.subtopic}
                                    </span>
                                  )}
                                </div>
                                {trimmed && (
                                  <p
                                    style={{
                                      fontFamily: '"Newsreader", serif',
                                      fontStyle: 'italic',
                                      fontSize: '11.5px',
                                      color: T.inkSoft,
                                      lineHeight: 1.45,
                                    }}
                                  >
                                    {trimmed}
                                  </p>
                                )}
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>

                    {/* Footer con confirmar */}
                    {selectedQuestionIds.size > 0 && (
                      <div
                        style={{
                          padding: '10px 12px',
                          background: T.paperWarm,
                          borderTop: `1px solid ${T.hairline}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px',
                        }}
                      >
                        <span className="mep-q-cap" style={{ color: T.inkMute }}>
                          <span style={{ color: T.accent, fontWeight: 600 }}>
                            {selectedQuestionIds.size}
                          </span>
                          {' '}preguntas en filtro
                        </span>
                        <button
                          onClick={() => setShowQuestionDropdown(false)}
                          className="mep-q-btn"
                          data-tone="accent"
                          style={{ padding: '5px 14px', fontSize: '11px' }}
                        >
                          aplicar →
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}

        {/* Modo Estadísticas vs práctica */}
        {!reviewMode ? (
          <>
            {/* Navegación de preguntas en modo estudio */}
            {!simulationMode && (
              <QuestionNavigation
                questions={filteredQuestions}
                currentQuestion={currentQuestion}
                setCurrentQuestion={setCurrentQuestion}
                darkMode={darkMode}
                sortByYear={sortByYear}
                setSortByYear={setSortByYear}
              />
            )}

            {/* Contenido de la pregunta — hero editorial */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentQuestion}
                initial={{ opacity: 0, x: animateQuestion ? 16 : -16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: animateQuestion ? -16 : 16 }}
                transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                className="mb-7"
              >
                {/* Meta line: año + AI badge + TTS */}
                <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
                  <div className="flex items-center gap-3">
                    {currentQuestionData.year && (
                      <span
                        style={{
                          fontFamily: '"Geist Mono", monospace',
                          fontSize: '11px',
                          color: T.inkMute,
                          letterSpacing: '0.04em',
                        }}
                      >
                        {currentQuestionData.year}
                      </span>
                    )}
                    {(currentQuestionData.source === 'ai' || currentQuestionData.ai_model) && (
                      <span
                        style={{
                          fontFamily: '"Manrope", sans-serif',
                          fontSize: '9.5px',
                          fontWeight: 600,
                          letterSpacing: '0.22em',
                          textTransform: 'uppercase',
                          padding: '3px 8px',
                          color: T.accent,
                          border: `1px solid ${T.accent}`,
                          background: T.accentTint,
                        }}
                      >
                        ⟢ {currentQuestionData.ai_model || 'IA'}
                      </span>
                    )}
                  </div>
                  <div>
                    <TTSControls
                      text={`${currentQuestionData.case_description || ''} ${currentQuestionData.question_text}`}
                      darkMode={darkMode}
                      questionId={currentQuestionData.id}
                    />
                  </div>
                </div>

                {/* Case description — bloque editorial cómodo de leer */}
                {currentQuestionData.case_description && (
                  <div className="mb-8" style={{ position: 'relative' }}>
                    {/* Marca lateral discreta */}
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: '8px',
                        bottom: '8px',
                        width: '3px',
                        background: `linear-gradient(180deg, ${T.accent} 0%, ${T.accent}40 100%)`,
                      }}
                    />
                    <div
                      style={{
                        paddingLeft: 'clamp(22px, 3.2vw, 36px)',
                        paddingRight: 'clamp(8px, 1.5vw, 16px)',
                        color: T.inkSoft,
                        fontFamily: '"Newsreader", "Instrument Serif", serif',
                        fontStyle: 'italic',
                        fontWeight: 400,
                        fontSize: 'clamp(17px, 2.05vw, 20px)',
                        lineHeight: 1.7,
                        letterSpacing: '0.003em',
                        maxWidth: '70ch',
                        // Optical size variable — Newsreader se ve mejor con opsz alta en tamaños medios
                        fontVariationSettings: '"opsz" 18',
                        // hyphens para mejor balance de líneas en español
                        hyphens: 'auto',
                        textAlign: 'left',
                      }}
                    >
                      <InteractiveCaseDescription
                        questionId={currentQuestionData.id}
                        text={currentQuestionData.case_description}
                        darkMode={darkMode}
                      />
                    </div>
                  </div>
                )}

                {/* Question text — statement principal, más presencia */}
                <div
                  className="mb-9"
                  style={{
                    color: T.inkBody,
                    fontFamily: '"Newsreader", "Instrument Serif", serif',
                    fontWeight: 450,
                    fontSize: 'clamp(19px, 2.35vw, 23px)',
                    lineHeight: 1.5,
                    letterSpacing: '-0.01em',
                    maxWidth: '64ch',
                    // Optical size ajustado al nuevo tamaño
                    fontVariationSettings: '"opsz" 24',
                    // Mejor render del serif en pantalla
                    WebkitFontSmoothing: 'antialiased',
                    textRendering: 'optimizeLegibility',
                  }}
                >
                  <InteractiveQuestionText
                    questionId={currentQuestionData.id}
                    text={currentQuestionData.question_text}
                    darkMode={darkMode}
                  />
                </div>

                {/* Opciones — rows premium */}
                <div className="space-y-2">
                  {currentQuestionData.options.map((option) => {
                    const isSelected = currentAnswerId === option.id;
                    const isAttempted = attemptedIncorrect.includes(option.id);
                    const showCorrectReveal = !simulationMode && option.correct && showExplanation;

                    let dataState = '';
                    if (showCorrectReveal) dataState = 'correct';
                    else if (!simulationMode && isAttempted) dataState = 'incorrect';
                    else if (simulationMode && isSelected) dataState = 'selected';

                    return (
                      <div key={option.id} className="relative">
                        <CelebrationBurst show={celebrationOptionId === option.id} />

                        <button
                          onClick={() => {
                            if (simulationMode && isPaused) return;
                            if (simulationMode) {
                              setAnswerByOptionId(option.id);
                            } else if (!showExplanation && !isAttempted) {
                              handleAnswer(option.id);
                            }
                          }}
                          disabled={(simulationMode && isPaused) || (!simulationMode && (showExplanation || isAttempted))}
                          data-state={dataState || undefined}
                          className="mep-q-option flex items-start gap-3"
                        >
                          <RippleEffect isActive={rippleOptionId === option.id} darkMode={darkMode} />
                          <span className="mep-q-option-label" style={{ position: 'relative', zIndex: 2 }}>
                            {option.option_label}
                          </span>
                          <span style={{ flex: 1, position: 'relative', zIndex: 2 }}>
                            {option.option_text}
                          </span>
                          {dataState === 'correct' && (
                            <span style={{ position: 'relative', zIndex: 2, color: T.sage }}>
                              <Check size={18} />
                            </span>
                          )}
                          {dataState === 'incorrect' && (
                            <span style={{ position: 'relative', zIndex: 2, color: T.crimson }}>
                              <X size={16} />
                            </span>
                          )}
                          {dataState === 'selected' && (
                            <span
                              style={{
                                position: 'relative',
                                zIndex: 2,
                                fontFamily: '"Manrope", sans-serif',
                                fontSize: '9px',
                                fontWeight: 600,
                                letterSpacing: '0.22em',
                                textTransform: 'uppercase',
                                color: T.accent,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              ● tu respuesta
                            </span>
                          )}
                        </button>

                        {/* Trampa explicada — modo estudio incorrecto */}
                        {!simulationMode && isAttempted && option.incorrect_explanation && (
                          <motion.div
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                            className="mt-2 px-4 py-3 flex items-start gap-3"
                            style={{
                              background: T.crimsonSoft,
                              borderLeft: `2px solid ${T.crimson}`,
                            }}
                          >
                            <AlertCircle size={14} style={{ color: T.crimson, marginTop: '3px', flexShrink: 0 }} />
                            <div>
                              <p className="mep-q-cap mb-1" style={{ color: T.crimson }}>
                                ⟢ trampa
                              </p>
                              <p
                                style={{
                                  color: T.ink,
                                  fontFamily: '"Newsreader", serif',
                                  fontStyle: 'italic',
                                  fontSize: '14px',
                                  lineHeight: 1.55,
                                }}
                              >
                                {option.incorrect_explanation}
                              </p>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Panel de Notas de Coaching — editorial */}
            {simulationMode && !showResults && showNotePanel && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="mt-5 px-5 py-4"
                style={{
                  background: T.paperSoft,
                  border: `1px solid ${T.hairline}`,
                  borderLeft: `2px solid ${T.accent}`,
                }}
              >
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <p className="mep-q-cap" style={{ color: T.accent, letterSpacing: '0.28em' }}>
                    ⟢ nota · pregunta {currentQuestion + 1}
                  </p>
                  <div className="flex items-center gap-1.5">
                    <span className="mep-q-cap mr-1" style={{ color: T.inkMute }}>confianza:</span>
                    {[
                      { val: 1, label: 'seguro', color: T.sage },
                      { val: 2, label: 'duda', color: T.accent },
                      { val: 3, label: 'no sé', color: T.crimson },
                    ].map((opt) => {
                      const currentConf = questionNotes[currentQuestion]?.confidence;
                      const isActive = currentConf === opt.val;
                      return (
                        <button
                          key={opt.val}
                          onClick={() => {
                            setQuestionNotes((prev) => ({
                              ...prev,
                              [currentQuestion]: {
                                ...prev[currentQuestion],
                                text: prev[currentQuestion]?.text || '',
                                confidence: isActive ? null : opt.val,
                              },
                            }));
                          }}
                          style={{
                            padding: '4px 10px',
                            fontFamily: '"Manrope", sans-serif',
                            fontSize: '10px',
                            fontWeight: 600,
                            letterSpacing: '0.16em',
                            textTransform: 'uppercase',
                            color: isActive ? (darkMode ? '#07090D' : T.paper) : opt.color,
                            background: isActive ? opt.color : 'transparent',
                            border: `1px solid ${opt.color}`,
                            cursor: 'pointer',
                            transition: 'all 0.18s',
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <textarea
                  value={questionNotes[currentQuestion]?.text || ''}
                  onChange={(e) => {
                    setQuestionNotes((prev) => ({
                      ...prev,
                      [currentQuestion]: {
                        ...prev[currentQuestion],
                        confidence: prev[currentQuestion]?.confidence ?? null,
                        text: e.target.value,
                      },
                    }));
                  }}
                  placeholder="Tu razonamiento, dudas o enfoque para esta pregunta..."
                  rows={3}
                  className="w-full mep-q-input"
                  style={{
                    fontFamily: '"Newsreader", serif',
                    fontStyle: 'italic',
                    fontSize: '14px',
                    lineHeight: 1.55,
                    resize: 'vertical',
                  }}
                />
              </motion.div>
            )}

            {/* Explicación (modo estudio) — bloque editorial */}
            {showExplanation && !simulationMode && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="mt-7 mep-q-explanation"
              >
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="mep-q-cap" style={{ color: T.accent, letterSpacing: '0.32em' }}>
                    ⟢ explicación
                  </span>
                  <div className="flex-1" style={{ height: '1px', background: T.hairlineSoft }} />
                </div>
                <p
                  style={{
                    color: T.ink,
                    fontFamily: '"Newsreader", serif',
                    fontSize: '15px',
                    lineHeight: 1.65,
                    marginBottom: '20px',
                  }}
                >
                  {currentQuestionData.explanation}
                </p>

                {currentQuestionData.key_points && (
                  <>
                    <p className="mep-q-cap mb-2" style={{ color: T.inkMute }}>puntos clave</p>
                    <pre
                      style={{
                        background: T.paper,
                        border: `1px solid ${T.hairlineSoft}`,
                        padding: '12px 14px',
                        marginBottom: '16px',
                        fontFamily: '"Geist Mono", monospace',
                        fontSize: '12px',
                        color: T.ink,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {currentQuestionData.key_points}
                    </pre>
                  </>
                )}

                {currentQuestionData.ascii_diagram && (
                  <>
                    <p className="mep-q-cap mb-2" style={{ color: T.inkMute }}>esquema</p>
                    <pre
                      style={{
                        background: T.paper,
                        border: `1px solid ${T.hairlineSoft}`,
                        padding: '12px 14px',
                        marginBottom: '16px',
                        fontFamily: '"Geist Mono", monospace',
                        fontSize: '11.5px',
                        color: T.ink,
                        overflowX: 'auto',
                      }}
                    >
                      {currentQuestionData.ascii_diagram}
                    </pre>
                  </>
                )}

                {TableComponent && (
                  <div className="mt-4">
                    <button
                      onClick={() => setShowSubtopicTable(!showSubtopicTable)}
                      className="mep-q-btn w-full justify-center"
                      style={{ padding: '10px 14px' }}
                    >
                      {showSubtopicTable ? 'ocultar tabla interactiva' : 'ver tabla interactiva →'}
                    </button>
                    {showSubtopicTable && <div className="mt-4">{TableComponent}</div>}
                  </div>
                )}

                {EsquemaComponent && (
                  <div className="mt-4">
                    <button
                      onClick={() => setShowSubtopicEsquema(!showSubtopicEsquema)}
                      className="mep-q-btn w-full justify-center"
                      style={{ padding: '10px 14px' }}
                    >
                      {showSubtopicEsquema ? 'ocultar esquema' : 'ver esquema interactivo →'}
                    </button>
                    {showSubtopicEsquema && <div className="mt-4">{EsquemaComponent}</div>}
                  </div>
                )}

                <LearnMnemonicSection question={currentQuestionData} darkMode={darkMode} />

                {DecisionTableComponents && DecisionTableComponents.length > 0 && (
                  <>
                    <button
                      onClick={() => setShowDecisionTable(!showDecisionTable)}
                      className="mep-q-btn w-full justify-center mt-4"
                      style={{ padding: '10px 14px' }}
                    >
                      {showDecisionTable ? 'ocultar tabla(s) de decisión' : 'ver tabla(s) de decisión →'}
                    </button>
                    {showDecisionTable && DecisionTableComponents.map((Comp, idx) => (
                      <div key={idx} className="mt-4">
                        <Comp darkMode={darkMode} />
                      </div>
                    ))}
                  </>
                )}

                {/* CTA siguiente — primaria editorial */}
                <button
                  onClick={nextQuestion}
                  className="w-full mt-6 flex items-center justify-between"
                  style={{
                    padding: '14px 22px',
                    background: T.accent,
                    color: darkMode ? '#07090D' : T.paper,
                    fontFamily: '"Manrope", sans-serif',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    fontSize: '13.5px',
                    textTransform: 'uppercase',
                    border: `1px solid ${T.accent}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    ...(darkMode && { boxShadow: `0 0 20px ${T.glow}` }),
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = T.accentDeep;
                    e.currentTarget.style.borderColor = T.accentDeep;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = T.accent;
                    e.currentTarget.style.borderColor = T.accent;
                  }}
                >
                  <span>Siguiente pregunta</span>
                  <ChevronRight size={16} />
                </button>
              </motion.div>
            )}
          </>
        ) : (
          // Modo revisión de Estadísticas
          <div className="space-y-4">
            <h2 className={`text-xl font-bold ${darkMode ? 'text-gray-200' : 'text-gray-800'} mb-4`}>
              Estadísticas por Pregunta
            </h2>
            {questions.map((question) => {
              const stats =
                studyHistory.questionHistory[String(question.id)] || {
                  attempts: 0,
                  correct: 0,
                  lastAttempt: null
                };
              const effectiveness =
                stats.attempts > 0
                  ? Math.round((stats.correct / stats.attempts) * 100)
                  : 0;

              return (
                <div key={question.id} className={`p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg`}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="font-medium">
                      Pregunta {question.id} ({question.year})
                    </div>
                    <div
                      className={`text-sm ${
                        effectiveness >= 70
                          ? darkMode ? 'text-cyan-400 drop-shadow-[0_0_8px_rgba(6,182,212,0.5)]' : 'text-sky-600 font-semibold'
                          : effectiveness >= 40
                          ? darkMode ? 'text-yellow-400' : 'text-yellow-600'
                          : darkMode ? 'text-red-400' : 'text-red-600'
                      }`}
                    >
                      {effectiveness}% efectiva
                    </div>
                  </div>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                    Intentos: {stats.attempts} | Correctas: {stats.correct}
                  </p>
                  {stats.lastAttempt && (
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Último intento:{' '}
                      {new Date(stats.lastAttempt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Panel de Notas (modificado) */}
        {currentQuestionData && <NotesSidePanel questionId={currentQuestionData.id} darkMode={darkMode} />}
        
        {/* Modal de Login — editorial */}
        {showLoginModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)' }}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0, y: 8 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-md w-full relative"
              style={{
                background: T.paperSoft,
                border: `1px solid ${T.hairline}`,
                padding: '40px 36px 32px',
              }}
            >
              <div className="absolute top-0 left-0 w-7 h-px" style={{ background: T.accent }} />
              <div className="absolute top-0 left-0 w-px h-7" style={{ background: T.accent }} />
              <div className="absolute top-0 right-0 w-7 h-px" style={{ background: T.accent }} />
              <div className="absolute top-0 right-0 w-px h-7" style={{ background: T.accent }} />

              <p className="mep-q-cap mb-2" style={{ color: T.accent, letterSpacing: '0.32em' }}>
                ⟢ acceso requerido
              </p>
              <h3
                style={{
                  fontFamily: '"Newsreader", serif',
                  fontSize: '1.75rem',
                  color: T.ink,
                  fontWeight: 400,
                  letterSpacing: '-0.01em',
                  marginBottom: '12px',
                }}
              >
                Has alcanzado tu límite
              </h3>
              <p
                style={{
                  color: T.inkSoft,
                  fontFamily: '"Newsreader", serif',
                  fontStyle: 'italic',
                  fontSize: '14px',
                  lineHeight: 1.55,
                  marginBottom: '24px',
                }}
              >
                {upgradeMessage}
              </p>

              <button
                onClick={() => {
                  setShowLoginModal(false);
                  window.dispatchEvent(new CustomEvent('openLoginModal'));
                }}
                className="w-full flex items-center justify-between"
                style={{
                  padding: '14px 22px',
                  background: T.accent,
                  color: darkMode ? '#07090D' : T.paper,
                  fontFamily: '"Manrope", sans-serif',
                  fontWeight: 600,
                  fontSize: '12.5px',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  border: `1px solid ${T.accent}`,
                  cursor: 'pointer',
                  marginBottom: '10px',
                  transition: 'all 0.2s',
                  ...(darkMode && { boxShadow: `0 0 20px ${T.glow}` }),
                }}
              >
                <span>Iniciar sesión / registrarse</span>
                <ChevronRight size={14} />
              </button>
              <button
                onClick={() => setShowLoginModal(false)}
                className="mep-q-btn w-full justify-center"
                style={{ padding: '10px 16px' }}
              >
                cerrar
              </button>
            </motion.div>
          </div>
        )}

        {/* Modal de Upgrade PRO — editorial */}
        {showUpgradeModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)' }}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0, y: 8 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-lg w-full relative"
              style={{
                background: T.paperSoft,
                border: `1px solid ${T.hairline}`,
                padding: '40px 36px 32px',
              }}
            >
              <div className="absolute top-0 left-0 w-7 h-px" style={{ background: T.accent }} />
              <div className="absolute top-0 left-0 w-px h-7" style={{ background: T.accent }} />
              <div className="absolute top-0 right-0 w-7 h-px" style={{ background: T.accent }} />
              <div className="absolute top-0 right-0 w-px h-7" style={{ background: T.accent }} />
              <div className="absolute bottom-0 left-0 w-7 h-px" style={{ background: T.accent }} />
              <div className="absolute bottom-0 left-0 w-px h-7" style={{ background: T.accent }} />
              <div className="absolute bottom-0 right-0 w-7 h-px" style={{ background: T.accent }} />
              <div className="absolute bottom-0 right-0 w-px h-7" style={{ background: T.accent }} />

              <p className="mep-q-cap mb-2" style={{ color: T.accent, letterSpacing: '0.32em' }}>
                ⟢ plan pro
              </p>
              <h3
                style={{
                  fontFamily: '"Newsreader", serif',
                  fontStyle: 'italic',
                  fontSize: '2rem',
                  color: T.ink,
                  fontWeight: 400,
                  letterSpacing: '-0.01em',
                  marginBottom: '12px',
                }}
              >
                Sin límites para tu preparación.
              </h3>
              <p
                style={{
                  color: T.inkSoft,
                  fontFamily: '"Newsreader", serif',
                  fontStyle: 'italic',
                  fontSize: '14px',
                  lineHeight: 1.55,
                  marginBottom: '24px',
                }}
              >
                {upgradeMessage}
              </p>

              <div
                className="px-5 py-4 mb-6"
                style={{
                  background: T.accentTint,
                  border: `1px solid ${T.hairline}`,
                  borderLeft: `2px solid ${T.accent}`,
                }}
              >
                <div className="flex items-baseline justify-between mb-3">
                  <span className="mep-q-cap" style={{ color: T.inkMute }}>plan pro</span>
                  <span
                    style={{
                      fontFamily: '"Geist Mono", monospace',
                      fontSize: '20px',
                      color: T.accent,
                      fontWeight: 500,
                    }}
                  >
                    S/ 39.90
                    <span style={{ fontSize: '11px', color: T.inkSoft, marginLeft: 4 }}>/mes</span>
                  </span>
                </div>
                <ul className="space-y-2">
                  {[
                    'Preguntas ilimitadas',
                    'Modo simulacro completo',
                    'Estadísticas avanzadas',
                    'Explicaciones detalladas',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3" style={{ color: T.ink }}>
                      <Check size={13} style={{ color: T.sage, flexShrink: 0 }} />
                      <span style={{ fontFamily: '"Manrope", sans-serif', fontSize: '13.5px' }}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  window.location.href = '/pricing';
                }}
                className="w-full flex items-center justify-between"
                style={{
                  padding: '14px 22px',
                  background: T.accent,
                  color: darkMode ? '#07090D' : T.paper,
                  fontFamily: '"Manrope", sans-serif',
                  fontWeight: 600,
                  fontSize: '12.5px',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  border: `1px solid ${T.accent}`,
                  cursor: 'pointer',
                  marginBottom: '10px',
                  transition: 'all 0.2s',
                  ...(darkMode && { boxShadow: `0 0 20px ${T.glow}` }),
                }}
              >
                <span>Actualizar a PRO</span>
                <ChevronRight size={14} />
              </button>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="mep-q-btn w-full justify-center"
                style={{ padding: '10px 16px' }}
              >
                continuar con plan gratuito
              </button>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ==========================================================================
   COMPONENTE: Texto Interactivo (Pregunta)
   Se elimina el uso de botones "S" y "C". Ahora se usa:
     - Click simple para subrayar (amarillo).
     - Doble click para encircular (rojo).
   Además se agrega un botón para limpiar resaltados.
   ==========================================================================
*/
function InteractiveQuestionText({ text, questionId, darkMode }) {
  // Dividimos en palabras
  // Memorizar el array de palabras para que no cambie en cada render
  const words = useMemo(() => text.split(' '), [text]);

  // Estado de estilos: se inicializa o reinicializa cada vez que cambia questionId o text.
  const [wordStyles, setWordStyles] = useState(() => {
    const saved = localStorage.getItem(`question_text_highlights_${questionId}`);
    if (saved) {
      return JSON.parse(saved);
    }
    return words.map(() => ({ underline: false, circle: false }));
  });

  // Reinicializar el estado cuando cambian questionId o text.
  useEffect(() => {
    const saved = localStorage.getItem(`question_text_highlights_${questionId}`);
    if (saved) {
      setWordStyles(JSON.parse(saved));
    } else {
      const initialStyles = words.map(() => ({ underline: false, circle: false }));
      setWordStyles(initialStyles);
    }
  }, [questionId, text, words]);

  const saveStyles = (newStyles) => {
    localStorage.setItem(`question_text_highlights_${questionId}`, JSON.stringify(newStyles));
  };

  const clickTimerRef = useRef(null);

  const toggleUnderline = (index) => {
    setWordStyles((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        underline: !updated[index].underline
      };
      saveStyles(updated);
      return updated;
    });
  };

  const toggleCircle = (index) => {
    setWordStyles((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        circle: !updated[index].circle
      };
      saveStyles(updated);
      return updated;
    });
  };

  const handleClick = (index) => {
    if (clickTimerRef.current) return;
    clickTimerRef.current = setTimeout(() => {
      toggleUnderline(index);
      clickTimerRef.current = null;
    }, 200);
  };

  const handleDoubleClick = (index) => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    toggleCircle(index);
  };

  // Botón para limpiar estilos
  const clearStyles = () => {
    const cleared = words.map(() => ({ underline: false, circle: false }));
    setWordStyles(cleared);
    saveStyles(cleared);
  };

  const getStyle = (styles) => {
    let style = {
      cursor: 'pointer',
      position: 'relative',
      transition: 'all 0.2s ease',
      display: 'inline-block',
      padding: '2px 4px',
      margin: '0 1px',
      borderRadius: '3px'
    };
    
    if (styles.underline) {
      // Resaltado amarillo - ajustado para modo oscuro
      if (darkMode) {
        style.background = 'linear-gradient(105deg, transparent 0%, rgba(252, 211, 77, 0.15) 2%, rgba(252, 211, 77, 0.35) 10%, rgba(252, 211, 77, 0.35) 90%, rgba(252, 211, 77, 0.15) 98%, transparent 100%)';
        style.boxShadow = '0 2px 4px rgba(252, 211, 77, 0.15)';
      } else {
        style.background = 'linear-gradient(105deg, transparent 0%, rgba(254, 240, 138, 0.3) 2%, rgba(254, 240, 138, 0.9) 10%, rgba(254, 240, 138, 0.9) 90%, rgba(254, 240, 138, 0.3) 98%, transparent 100%)';
        style.boxShadow = '0 2px 4px rgba(252, 211, 77, 0.2)';
      }
    }
    
    if (styles.circle) {
      // Resaltado rojo - ajustado para modo oscuro
      if (darkMode) {
        style.background = 'linear-gradient(105deg, transparent 0%, rgba(239, 68, 68, 0.15) 2%, rgba(239, 68, 68, 0.35) 10%, rgba(239, 68, 68, 0.35) 90%, rgba(239, 68, 68, 0.15) 98%, transparent 100%)';
        style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.15)';
      } else {
        style.background = 'linear-gradient(105deg, transparent 0%, rgba(252, 165, 165, 0.3) 2%, rgba(252, 165, 165, 0.9) 10%, rgba(252, 165, 165, 0.9) 90%, rgba(252, 165, 165, 0.3) 98%, transparent 100%)';
        style.boxShadow = '0 2px 4px rgba(248, 113, 113, 0.2)';
      }
    }
    
    return style;
  };
  
  const getClassName = (styles) => {
    let baseClass = 'cursor-pointer relative transition-all duration-200';
    if (styles.underline) {
      return baseClass + ' highlight-yellow';
    }
    if (styles.circle) {
      return baseClass + ' highlight-red';
    }
    return baseClass;
  };

  return (
    <div style={{ lineHeight: 'inherit', fontSize: 'inherit' }}>
      {words.map((word, i) => {
        const styles = wordStyles[i] || { underline: false, circle: false };
        return (
          <span
            key={i}
            className={getClassName(styles)}
            style={getStyle(styles)}
            onClick={() => handleClick(i)}
            onDoubleClick={() => handleDoubleClick(i)}
          >
            {word}{' '}
          </span>
        );
      })}
      <button
        onClick={clearStyles}
        className={`ml-2 px-1.5 py-0.5 bg-transparent ${darkMode ? 'border-gray-600 text-gray-500 hover:text-gray-300 hover:border-gray-500' : 'border-gray-300 text-gray-400 hover:text-gray-600 hover:border-gray-400'} border text-[10px] rounded transition-colors`}
        style={{ fontFamily: '"Manrope", sans-serif', letterSpacing: '0.04em' }}
      >
        limpiar resaltados
      </button>
    </div>
  );
}

/* ==========================================================================
   COMPONENTE: Texto Interactivo (Case Description)
   Dinámica similar a InteractiveQuestionText pero hereda styling del wrapper.
   ==========================================================================
*/
function InteractiveCaseDescription({ text, questionId, darkMode }) {
  // Dividimos en palabras
  // Memorizar el array de palabras para que no cambie en cada render
  const words = useMemo(() => text.split(' '), [text]);

  // Estado de estilos: se inicializa o reinicializa cada vez que cambia questionId o text.
  const [wordStyles, setWordStyles] = useState(() => {
    const saved = localStorage.getItem(`case_desc_highlights_${questionId}`);
    if (saved) {
      return JSON.parse(saved);
    }
    return words.map(() => ({ underline: false, circle: false }));
  });

  // Reinicializar el estado cuando cambian questionId o text.
  useEffect(() => {
    const saved = localStorage.getItem(`case_desc_highlights_${questionId}`);
    if (saved) {
      setWordStyles(JSON.parse(saved));
    } else {
      const initialStyles = words.map(() => ({ underline: false, circle: false }));
      setWordStyles(initialStyles);
    }
  }, [questionId, text, words]);

  const saveStyles = (newStyles) => {
    localStorage.setItem(`case_desc_highlights_${questionId}`, JSON.stringify(newStyles));
  };

  const clickTimerRef = useRef(null);

  const toggleUnderline = (index) => {
    setWordStyles((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        underline: !updated[index].underline
      };
      saveStyles(updated);
      return updated;
    });
  };

  const toggleCircle = (index) => {
    setWordStyles((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        circle: !updated[index].circle
      };
      saveStyles(updated);
      return updated;
    });
  };

  const handleClick = (index) => {
    if (clickTimerRef.current) return;
    clickTimerRef.current = setTimeout(() => {
      toggleUnderline(index);
      clickTimerRef.current = null;
    }, 200);
  };

  const handleDoubleClick = (index) => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    toggleCircle(index);
  };

  const clearStyles = () => {
    const cleared = words.map(() => ({ underline: false, circle: false }));
    setWordStyles(cleared);
    saveStyles(cleared);
  };

  const getStyle = (styles) => {
    let style = {
      cursor: 'pointer',
      position: 'relative',
      transition: 'all 0.2s ease',
      display: 'inline-block',
      padding: '2px 4px',
      margin: '0 1px',
      borderRadius: '3px'
    };
    
    if (styles.underline) {
      // Resaltado amarillo - ajustado para modo oscuro
      if (darkMode) {
        style.background = 'linear-gradient(105deg, transparent 0%, rgba(252, 211, 77, 0.15) 2%, rgba(252, 211, 77, 0.35) 10%, rgba(252, 211, 77, 0.35) 90%, rgba(252, 211, 77, 0.15) 98%, transparent 100%)';
        style.boxShadow = '0 2px 4px rgba(252, 211, 77, 0.15)';
      } else {
        style.background = 'linear-gradient(105deg, transparent 0%, rgba(254, 240, 138, 0.3) 2%, rgba(254, 240, 138, 0.9) 10%, rgba(254, 240, 138, 0.9) 90%, rgba(254, 240, 138, 0.3) 98%, transparent 100%)';
        style.boxShadow = '0 2px 4px rgba(252, 211, 77, 0.2)';
      }
    }
    
    if (styles.circle) {
      // Resaltado rojo - ajustado para modo oscuro
      if (darkMode) {
        style.background = 'linear-gradient(105deg, transparent 0%, rgba(239, 68, 68, 0.15) 2%, rgba(239, 68, 68, 0.35) 10%, rgba(239, 68, 68, 0.35) 90%, rgba(239, 68, 68, 0.15) 98%, transparent 100%)';
        style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.15)';
      } else {
        style.background = 'linear-gradient(105deg, transparent 0%, rgba(252, 165, 165, 0.3) 2%, rgba(252, 165, 165, 0.9) 10%, rgba(252, 165, 165, 0.9) 90%, rgba(252, 165, 165, 0.3) 98%, transparent 100%)';
        style.boxShadow = '0 2px 4px rgba(248, 113, 113, 0.2)';
      }
    }
    
    return style;
  };
  
  const getClassName = (styles) => {
    let baseClass = 'cursor-pointer relative transition-all duration-200';
    if (styles.underline) {
      return baseClass + ' highlight-yellow';
    }
    if (styles.circle) {
      return baseClass + ' highlight-red';
    }
    return baseClass;
  };

  return (
    <div style={{ lineHeight: 'inherit', fontSize: 'inherit', fontStyle: 'inherit' }}>
      {words.map((word, i) => {
        const styles = wordStyles[i] || { underline: false, circle: false };
        return (
          <span
            key={i}
            className={getClassName(styles)}
            style={getStyle(styles)}
            onClick={() => handleClick(i)}
            onDoubleClick={() => handleDoubleClick(i)}
          >
            {word}{' '}
          </span>
        );
      })}
      <button
        onClick={clearStyles}
        className={`ml-2 px-1.5 py-0.5 bg-transparent ${darkMode ? 'border-gray-600 text-gray-500 hover:text-gray-300 hover:border-gray-500' : 'border-gray-300 text-gray-400 hover:text-gray-600 hover:border-gray-400'} border text-[10px] rounded transition-colors`}
        style={{ fontFamily: '"Manrope", sans-serif', letterSpacing: '0.04em' }}
      >
        limpiar resaltados
      </button>
    </div>
  );
}

/* ==========================================================================
   COMPONENTE: Panel lateral de Notas
   Ahora se asegura de usar `notes_${questionId}` para cada pregunta.
   ==========================================================================
*/
function NotesSidePanel({ questionId, darkMode }) {
  // Clave en localStorage
  const storageKey = `notes_${questionId}`;

  // Estado: mostrar/ocultar panel
  const [showPanel, setShowPanel] = useState(false);

  // Estado: contenido de las notas
  const [notes, setNotes] = useState('');
  const [saved, setSaved] = useState(false);

  // Cargar notas guardadas al iniciar
  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      setNotes(saved);
    } else {
      setNotes('');
    }
  }, [storageKey]);

  // Guardar en localStorage
  const handleSaveNotes = () => {
    localStorage.setItem(storageKey, notes);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <>
      {/* Botón flotante mejorado para abrir/cerrar el panel */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setShowPanel(!showPanel)}
        className={`fixed bottom-4 right-4 z-50 w-12 h-12 flex items-center justify-center rounded-full shadow-lg transition-all duration-300 ${
          darkMode 
            ? 'bg-gradient-to-br from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700' 
            : 'bg-gradient-to-br from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700'
        } text-white`}
        title={showPanel ? 'Cerrar Notas' : 'Abrir Notas'}
      >
        <svg 
          className={`w-5 h-5 transition-transform duration-300 ${showPanel ? 'rotate-45' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </motion.button>

      {/* Overlay para cerrar el panel al hacer clic fuera */}
      {showPanel && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30"
          onClick={() => setShowPanel(false)}
        />
      )}

      {/* Panel lateral mejorado con textarea */}
      <AnimatePresence>
        {showPanel && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className={`fixed top-0 right-0 h-full w-full sm:w-96 shadow-2xl z-40 ${
              darkMode ? 'bg-gray-900' : 'bg-white'
            }`}
          >
        <div className="flex flex-col h-full">
          {/* Header del panel */}
          <div className={`flex items-center justify-between p-4 border-b ${
            darkMode 
              ? 'bg-gradient-to-r from-purple-600 to-indigo-600 border-gray-700' 
              : 'bg-gradient-to-r from-blue-500 to-indigo-600 border-gray-200'
          } text-white`}>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h2 className="text-lg font-bold">Notas - Pregunta {questionId}</h2>
            </div>
            <button 
              onClick={() => setShowPanel(false)} 
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Contenido del panel */}
          <div className="flex-1 p-4 overflow-y-auto">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Escribe tus notas aquí..."
              className={`w-full h-64 p-3 border-2 rounded-lg resize-none transition-all duration-200 focus:outline-none focus:ring-2 ${
                darkMode 
                  ? 'bg-gray-800 text-white border-gray-700 focus:border-purple-500 focus:ring-purple-500/20' 
                  : 'bg-gray-50 text-gray-900 border-gray-300 focus:border-blue-500 focus:ring-blue-500/20'
              }`}
            />
            
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleSaveNotes}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] ${
                  darkMode 
                    ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700' 
                    : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
                } text-white`}
              >
                {saved ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Guardado
                  </span>
                ) : (
                  'Guardar'
                )}
              </button>
              
              <button
                onClick={() => setNotes('')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 border-2 ${
                  darkMode 
                    ? 'border-gray-700 text-gray-400 hover:bg-gray-800 hover:text-white' 
                    : 'border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                Limpiar
              </button>
            </div>
            
            {/* Indicador de caracteres */}
            <p className={`text-xs mt-2 text-right ${
              darkMode ? 'text-gray-500' : 'text-gray-400'
            }`}>
              {notes.length} caracteres
            </p>
          </div>
        </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* ==========================================================================
   COMPONENTE: Sección de Mnemotecnia / Aprendido
   ==========================================================================
*/
function LearnMnemonicSection({ question, darkMode }) {
  const ebData = getErrorBankData(String(question.id)) || {
    learned: false,
    mnemonic: ''
  };
  const [isLearned, setIsLearned] = useState(ebData.learned);
  const [mnemonicValue, setMnemonicValue] = useState(ebData.mnemonic || '');
  const [editingMnemonic, setEditingMnemonic] = useState(false);

  useEffect(() => {
    setIsLearned(ebData.learned);
    setMnemonicValue(ebData.mnemonic || '');
    setEditingMnemonic(false);
  }, [question.id, ebData.learned, ebData.mnemonic]);

  const handleToggleLearned = () => {
    toggleLearned(String(question.id));
    setIsLearned(!isLearned);
  };

  const handleSaveMnemonic = () => {
    updateMnemonic(String(question.id), mnemonicValue);
    setEditingMnemonic(false);
  };

  return (
    <div className={`mt-4 p-3 ${darkMode ? 'bg-gray-700' : 'bg-white'} rounded shadow-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-bold">¿Aprendido?</span>
        <button
          onClick={handleToggleLearned}
          className={`px-3 py-1 rounded text-white text-sm ${
            isLearned ? 'bg-red-500 hover:bg-red-600' : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {isLearned ? 'Marcar como NO aprendido' : 'Marcar como Aprendido'}
        </button>
      </div>

      <div className="mt-2">
        <span className="font-bold">Mnemotecnia:</span>
        {!editingMnemonic ? (
          <div className="mt-1">
            {mnemonicValue ? (
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} whitespace-pre-wrap`}>
                {mnemonicValue}
              </p>
            ) : (
              <p className={`text-sm italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No hay mnemotecnia guardada</p>
            )}
            <button
              onClick={() => setEditingMnemonic(true)}
              className={`mt-2 px-3 py-1 ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white text-sm rounded`}
            >
              {mnemonicValue ? 'Editar' : 'Agregar'}
            </button>
          </div>
        ) : (
          <div className="mt-1">
            <textarea
              rows={3}
              className={`w-full border ${darkMode ? 'bg-gray-800 text-white border-gray-600' : 'border-gray-300'} rounded p-1 text-sm`}
              value={mnemonicValue}
              onChange={(e) => setMnemonicValue(e.target.value)}
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={handleSaveMnemonic}
                className={`px-3 py-1 ${darkMode ? 'bg-green-700 hover:bg-green-800' : 'bg-green-600 hover:bg-green-700'} text-white text-sm rounded`}
              >
                Guardar
              </button>
              <button
                onClick={() => {
                  setMnemonicValue(ebData.mnemonic || '');
                  setEditingMnemonic(false);
                }}
                className={`px-3 py-1 ${darkMode ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-400 hover:bg-gray-500'} text-white text-sm rounded`}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ==========================================================================
   COMPONENTE: Navegación de Preguntas
   ==========================================================================
*/
function QuestionNavigation({ questions, currentQuestion, setCurrentQuestion, darkMode, sortByYear, setSortByYear }) {
  // Tokens locales
  const ink = darkMode ? '#F2EDE0' : '#0E1218';
  const inkMute = darkMode ? '#6A6356' : '#7A8499';
  const hairline = darkMode ? '#2B3140' : '#D8D2C5';
  const accent = darkMode ? '#7DA5D9' : '#1E3A6F';
  const paperSoft = darkMode ? '#0F131A' : '#FBF8F1';
  const glow = darkMode ? 'rgba(125, 165, 217, 0.22)' : 'rgba(30, 58, 111, 0.16)';

  return (
    <div className="mb-6">
      <div className="flex justify-between items-baseline mb-3 flex-wrap gap-2">
        <span
          style={{
            fontFamily: '"Manrope", sans-serif',
            fontSize: '10px',
            fontWeight: 600,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: inkMute,
          }}
        >
          ⟢ navegación · {questions.length} preguntas
        </span>
        {setSortByYear && (
          <button
            onClick={() => setSortByYear(!sortByYear)}
            style={{
              padding: '5px 11px',
              fontFamily: '"Manrope", sans-serif',
              fontSize: '11px',
              fontWeight: 500,
              letterSpacing: '0.02em',
              color: sortByYear ? (darkMode ? '#07090D' : paperSoft) : ink,
              background: sortByYear ? accent : 'transparent',
              border: `1px solid ${sortByYear ? accent : hairline}`,
              cursor: 'pointer',
              transition: 'all 0.18s',
            }}
          >
            {sortByYear ? '✓ por año' : 'ordenar por año ↓'}
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {questions.map((q, index) => {
          const isCurrent = index === currentQuestion;
          return (
            <button
              key={q.id}
              onClick={() => setCurrentQuestion(index)}
              style={{
                width: '32px',
                height: '32px',
                fontFamily: '"Geist Mono", monospace',
                fontVariantNumeric: 'tabular-nums',
                fontSize: '11.5px',
                fontWeight: 500,
                color: isCurrent ? (darkMode ? '#07090D' : paperSoft) : ink,
                background: isCurrent ? accent : 'transparent',
                border: `1px solid ${isCurrent ? accent : hairline}`,
                cursor: 'pointer',
                transition: 'all 0.18s',
                ...(isCurrent && darkMode && { boxShadow: `0 0 12px ${glow}` }),
              }}
              onMouseEnter={(e) => {
                if (isCurrent) return;
                e.currentTarget.style.borderColor = accent;
                e.currentTarget.style.color = accent;
              }}
              onMouseLeave={(e) => {
                if (isCurrent) return;
                e.currentTarget.style.borderColor = hairline;
                e.currentTarget.style.color = ink;
              }}
            >
              {index + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ==========================================================================
   FUNC: Guardar Examen en el Historial
   ==========================================================================
*/
function saveExamToHistory(examData, existingSessionId = null) {
  // ── PRIMARY: Save to MySQL ──
  const analytics = examData.analytics || {};
  const summary = analytics.summary || examData.summary || {};
  const questionSummaries = analytics.questionSummaries || examData.questionDetails || [];

  const answers = questionSummaries.map((qs, i) => {
    const q = qs.question || {};
    return {
      questionId: q.id || null,
      questionIndex: qs.index ?? i,
      questionText: q.question_text || null,
      caseDescription: q.case_description || null,
      area: qs.area || q.area || null,
      topic: qs.topic || q.topic || null,
      subtopic: qs.subtopic || q.subtopic || null,
      subtopicSpecific: qs.subtopicSpecific || q.subtopic_specific || null,
      organsystem: qs.organ || q.organsystem || null,
      especialidad: q.especialidad || null,
      year: q.year || null,
      userAnswerLabel: qs.userAnswerLabel || null,
      userAnswerText: qs.userAnswerText || null,
      correctAnswerLabel: qs.correctOptionLabel || null,
      correctAnswerText: qs.correctOptionText || null,
      status: qs.status || 'skipped',
      flagged: qs.flagged || false,
      note: qs.note || null,
      confidence: qs.confidence ?? null,
      timeSpentSeconds: qs.timeSpentSeconds ?? null,
    };
  });

  const payload = {
    examTitle: examData.examTitle || 'Examen sin Título',
    examType: examData.examType || 'simulacro',
    simulacroId: examData.simulacroId || null,
    totalQuestions: summary.totalQuestions || questionSummaries.length,
    correct: summary.correct || 0,
    incorrect: summary.incorrect || 0,
    skipped: summary.skipped || 0,
    accuracy: summary.accuracy || 0,
    timeSpentSeconds: examData.meta?.timeSpentSeconds || null,
    timeAllocatedSeconds: examData.meta?.allocatedSeconds || null,
    startedAt: examData.startedAt || null,
    completedAt: new Date().toISOString(),
    status: 'completed',
    answers,
  };

  const apiUrl = config.getApiUrl();

  // Use PUT if we have an existing in-progress session, POST otherwise
  const url = existingSessionId
    ? `${apiUrl}/exam-sessions/${existingSessionId}`
    : `${apiUrl}/exam-sessions`;
  const method = existingSessionId ? 'PUT' : 'POST';

  fetch(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
    .then((res) => res.json())
    .then((data) => {
      if (data.success || data.sessionId) {
        console.log(`[ExamHistory] ${existingSessionId ? 'Actualizado' : 'Guardado'} en MySQL (session ID: ${existingSessionId || data.sessionId})`);
      } else {
        console.error('[ExamHistory] Error MySQL:', data.error);
      }
    })
    .catch((err) => {
      console.error('[ExamHistory] No se pudo guardar en MySQL:', err);
    });

  // ── FALLBACK: Also save summary to localStorage (without heavy question data) ──
  const lightPayload = {
    ...examData,
    questionDetails: undefined,
    analytics: {
      summary: analytics.summary,
      areas: analytics.areas,
      areaChartData: analytics.areaChartData,
    },
    _savedToMySQL: true,
  };

  try {
    const stored = localStorage.getItem('examHistory');
    const current = stored ? JSON.parse(stored) : [];
    const updated = [lightPayload, ...current].slice(0, 12);
    localStorage.setItem('examHistory', JSON.stringify(updated));
  } catch (_) {
    // localStorage full — MySQL has the data
  }
}

export default Quiz;
